import type { Catalog } from "@/lib/i18n/translate";
import type { MessageKey } from "@/lib/i18n/messages.de";

/**
 * Swiss Italian UI texts (formal "Lei"), machine translated from German and open for
 * corrections. Amounts and dates follow Swiss conventions (see lib/locale). Tax terms come
 * from the country profile.
 */
export const it: Catalog<MessageKey> = {
  "app.name": "Rhinvoice",
  "app.skipToContent": "Vai al contenuto",
  "app.loading": "Caricamento …",
  "app.pageTitle": "{title} · Rhinvoice",

  "nav.main": "Navigazione principale",
  "nav.dashboard": "Panoramica",
  "nav.documents": "Documenti",
  "nav.invoices": "Fatture",
  "nav.quotes": "Offerte",
  "nav.customers": "Clienti",
  "nav.articles": "Articoli",
  "nav.settings": "Impostazioni",
  "nav.newInvoice": "Nuova fattura",
  "nav.more": "Altro",
  "nav.openMenu": "Apri menu",
  "nav.collapse": "Comprimi barra laterale",
  "nav.expand": "Espandi barra laterale",

  "topbar.search": "Cerca …",
  "topbar.help": "Aiuto e scorciatoie da tastiera",
  "topbar.account": "Account",
  "topbar.logout": "Esci",

  "theme.label": "Tema colori",
  "theme.light": "Chiaro",
  "theme.dark": "Scuro",
  "theme.system": "Sistema",
  "language.label": "Lingua",
  "language.machineTranslated": "Traduzione automatica – segnalazioni benvenute",
  "demo.title": "Demo",
  "demo.loginHint":
    "Provi Rhinvoice con dati di esempio. Tutti i visitatori condividono questa demo; viene reimpostata ogni {minutes} minuti e le e-mail non vengono inviate.",
  "demo.locked":
    "Non disponibile nella demo pubblica: qui tutti i visitatori accedono con lo stesso account e non vengono inviate e-mail.",
  "demo.signIn": "Apri la demo",
  "demo.otherCountries": "Disponibile anche come demo:",
  "demo.banner":
    "Dati e conti di esempio, non bonificare nulla · reimpostati ogni {minutes} minuti, nessuna e-mail",
  "demo.countryDemo": "Demo {country}",
  "about.title": "Informazioni su Rhinvoice",
  "about.version": "Versione {version}",
  "about.description":
    "Programma di fatturazione open source per Svizzera, Liechtenstein, Germania e Austria.",
  "about.license": "Licenza: GNU Affero General Public License v3",
  "about.source": "Codice sorgente",
  "about.reportBug": "Segnala un problema",
  "support.title": "Segnala un problema",
  "support.description":
    "Rhinvoice non invia nulla di propria iniziativa. Lei copia i dati e decide a chi inviarli.",
  "support.copyStep":
    "1. Copi il protocollo. Può contenere nomi di clienti: lo controlli brevemente prima di inviarlo.",
  "support.copy": "Copia protocollo",
  "support.copied": "Protocollo copiato",
  "support.copyFailed":
    "La copia non è riuscita. Selezioni il testo qui sotto e lo copi manualmente.",
  "support.serverStep":
    "1. Il protocollo si trova sul server. Esegua lì questo comando e copi l’output:",
  "support.sendStep": "2. Lo segnali su GitHub o lo invii per e-mail a {email}.",
  "support.github": "Segnala su GitHub",
  "about.changelog": "Novità",
  "about.noAdvice": "Nessuna consulenza fiscale, aliquote senza garanzia.",
  "about.machineTranslated":
    "Questa traduzione è stata generata automaticamente. Le correzioni sono benvenute:",
  "about.reportTranslation": "Segnala un errore di traduzione",

  "updates.title": "Aggiornamento",
  "updates.newVersion": "Nuova versione {version}",
  "updates.notes": "Cosa c’è di nuovo?",
  "updates.install": "Aggiorna ora",
  "updates.installHint":
    "Richiede circa mezzo minuto. Prima viene eseguito un backup dei suoi dati.",
  "updates.installing": "Download in corso …",
  "updates.restarting": "Rhinvoice si sta riavviando …",
  "updates.commandEnv": "Impostare nel file .env:",
  "updates.commandEnvIfPinned": "Solo se .env fissa una versione, impostare lì:",
  "updates.commandOnly": "Eseguire sul server:",
  "updates.commandRun": "Poi eseguire sul server:",
  "updates.setupTitle": "{version} richiede un nuovo setup",
  "updates.setupHint": "Da installare una sola volta, i suoi dati restano invariati.",
  "updates.setupDownload": "Scarica il setup",
  "updates.failedTitle": "L’aggiornamento a {version} non è riuscito",
  "updates.failedHint": "Rhinvoice {current} è di nuovo in funzione, i suoi dati sono invariati.",
  "updates.upToDate": "Rhinvoice è aggiornato.",
  "updates.notChecked": "Non è ancora stato verificato se esiste una nuova versione.",
  "updates.check": "Cerca aggiornamenti",
  "updates.checking": "Verifica in corso …",
  "updates.setupDownloading": "Download in corso … {received} di {total} MB",
  "updates.setupReady": "Scaricato e verificato.",
  "updates.setupInstall": "Installa",
  "updates.setupFailed": "Il download non è riuscito.",
  "updates.setupRetry": "Riprova",
  "updates.setupByHand": "Scaricare il setup manualmente",
  "updates.closeTitle": "Chiudere Rhinvoice per l’aggiornamento?",
  "updates.closeSetup":
    "Rhinvoice si chiude, installa {version} e poi si riavvia da solo. Ci vuole circa un minuto, i suoi dati restano invariati.",
  "updates.closeRestart":
    "Rhinvoice si riavvia per {version}. Ci vuole circa mezzo minuto, i suoi dati vengono salvati prima.",
  "updates.closeSaveFirst": "Salvi prima tutto ciò su cui sta lavorando.",
  "updates.closeUnsaved":
    "Ci sono modifiche non salvate. Scelga «Più tardi», le salvi e avvii di nuovo l’aggiornamento.",
  "updates.closeConfirm": "Chiudi e aggiorna",
  "updates.closeAnyway": "Chiudi comunque",
  "updates.later": "Più tardi",
  "updates.closing": "Rhinvoice si chiude per l’aggiornamento …",
  "about.madeBy": "made by {maker}",
  "about.legalNotice": "Note legali",
  "about.privacy": "Protezione dei dati",
  "about.openSource": "Open source",

  "palette.title": "Tavolozza dei comandi",
  "palette.placeholder": "Digiti un comando o un termine di ricerca …",
  "palette.empty": "Nessun risultato.",
  "palette.actions": "Azioni",
  "palette.navigation": "Navigazione",
  "palette.toggleTheme": "Cambia tema colori",
  "palette.shortcuts": "Mostra scorciatoie da tastiera",

  "shortcuts.title": "Scorciatoie da tastiera",
  "shortcuts.description":
    "Le scorciatoie a tasto singolo sono attive solo quando nessun campo di immissione ha il focus.",
  "shortcuts.groupGeneral": "Generale",
  "shortcuts.groupNavigation": "Navigazione",
  "shortcuts.palette": "Apri la tavolozza dei comandi",
  "shortcuts.search": "Vai alla ricerca",
  "shortcuts.newInvoice": "Nuova fattura",
  "shortcuts.save": "Salva",
  "shortcuts.help": "Questa panoramica",
  "shortcuts.close": "Chiudi la finestra di dialogo",
  "shortcuts.goDashboard": "Vai alla panoramica",
  "shortcuts.goInvoices": "Vai alle fatture",
  "shortcuts.goCustomers": "Vai ai clienti",
  "shortcuts.goArticles": "Vai agli articoli",
  "shortcuts.goSettings": "Vai alle impostazioni",
  "shortcuts.then": "poi",

  "auth.loginTitle": "Accedi",
  "auth.loginDescription": "Acceda con il suo indirizzo e-mail.",
  "auth.email": "Indirizzo e-mail",
  "auth.password": "Password",
  "auth.totpCode": "Codice di verifica",
  "auth.totpHint": "Codice a sei cifre della sua app di autenticazione.",
  "auth.submit": "Accedi",
  "auth.submitting": "Accesso in corso …",
  "auth.forgotPassword": "Password dimenticata?",
  "auth.reset.title": "Password dimenticata",
  "auth.reset.description":
    "Imposti una nuova password. Dopo, tutti gli accessi con quella vecchia vengono terminati.",
  "auth.reset.administrator":
    "La nuova password la imposta chi gestisce il server. Con un’installazione Docker, nella cartella di Rhinvoice:",
  "auth.reset.whichAddress": "Quale indirizzo e-mail? Questo comando mostra tutti gli account:",
  "auth.reset.file":
    "Rhinvoice crea un codice monouso nella sua cartella dati su questo computer e apre il file. Il file contiene anche gli indirizzi e-mail degli account.",
  "auth.reset.request": "Richiedi codice",
  "auth.reset.again": "Richiedi un nuovo codice",
  "auth.reset.opened": "Il file con il codice è aperto. Si trova qui:",
  "auth.reset.code": "Codice",
  "auth.reset.newPassword": "Nuova password",
  "auth.reset.save": "Salva password",
  "auth.reset.done": "La password è stata modificata. Acceda con la nuova password.",
  "auth.reset.toLogin": "Vai all’accesso",
  "auth.reset.back": "Torna all’accesso",
  "auth.setupTitle": "Configura Rhinvoice",
  "auth.setupDescription":
    "Crei il primo account utente. Avrà accesso a tutti i dati di questa installazione.",
  "auth.displayName": "Il suo nome",
  "auth.newPassword": "Password",
  "auth.passwordHint":
    "Almeno 12 caratteri. Una frase di più parole è sicura e facile da ricordare.",
  "auth.setupSubmit": "Crea account",
  "auth.loggedOut": "La disconnessione è avvenuta.",
  "auth.sessionExpired": "La sua sessione è scaduta. Acceda di nuovo.",
  "auth.demoReset":
    "La demo è stata reimpostata, come ogni {minutes} minuti. In questo modo tutti vengono disconnessi: acceda di nuovo.",

  "validation.required": "Compili questo campo.",
  "validation.email": "Inserisca un indirizzo e-mail valido, ad es. nome@azienda.ch.",
  "validation.passwordLength": "La password deve contenere almeno 12 caratteri.",
  "validation.totp": "Il codice è composto da 6 cifre.",
  "validation.summary": {
    one: "1 indicazione deve essere corretta",
    other: "{count} indicazioni devono essere corrette",
  },

  "error.demo_locked": "Nella demo pubblica questo non è possibile.",
  "error.update_running": "L’aggiornamento è già in corso di installazione.",
  "error.update_check_failed":
    "Non è stato possibile recuperare le informazioni sulle nuove versioni. Il computer è connesso a Internet?",
  "error.update_check_off": "La ricerca di aggiornamenti è disattivata in questa installazione.",
  "error.no_update": "Qui non c’è nessun aggiornamento da installare.",
  "error.generic": "Qualcosa è andato storto. Riprovi.",
  "error.network": "Nessuna connessione al server. Controlli la sua connessione.",
  "error.authentication_failed": "Indirizzo e-mail o password errati.",
  "error.session_expired": "La sua sessione è scaduta. Acceda di nuovo.",
  "error.not_logged_in": "Non ha effettuato l’accesso.",
  "error.reset_code_invalid":
    "L’indirizzo e-mail o il codice non è corretto, oppure il codice è scaduto. Dopo cinque tentativi falliti serve un nuovo codice.",
  "error.reset_by_administrator":
    "Qui la password viene reimpostata da un amministratore sul server.",
  "error.rate_limited": "Troppi tentativi da questo indirizzo. Attenda un minuto.",
  "error.account_locked": "Troppi tentativi falliti. L’account è bloccato per 15 minuti.",
  "error.totp_required": "Inserisca il codice di verifica.",
  "error.retry": "Riprova",
  "error.mail_not_configured":
    "L’invio di e-mail non è ancora configurato (Impostazioni → E-mail).",
  "error.mail_draft": "Le bozze non vengono inviate. Emetta prima il documento.",
  "error.mail_auth_failed": "Il server di posta ha rifiutato il nome utente o la password.",
  "error.mail_recipient_refused":
    "Il server di posta ha rifiutato un destinatario. Controlli l’indirizzo.",
  "error.mail_sender_refused":
    "Il server di posta non accetta l’indirizzo del mittente. Di solito deve appartenere all’account presso il provider.",
  "error.mail_tls_failed":
    "La connessione cifrata al server di posta non è riuscita. Controlli la porta e la cifratura.",
  "error.mail_timeout": "Il server di posta non ha risposto in tempo.",
  "error.mail_connection_failed":
    "Nessuna connessione al server di posta. Controlli il server e la porta.",
  "error.mail_failed": "Il server di posta non ha accettato l’e-mail.",
  "error.recipient_missing": "Indichi almeno un indirizzo e-mail.",
  "error.invalid_recipient": "Un indirizzo del destinatario non è valido.",
  "error.mail_partly_refused":
    "Il server di posta ha rifiutato una parte dei destinatari; gli altri hanno ricevuto l’e-mail (annotato nella cronologia).",
  "error.mail_too_large": "L’e-mail è più grande di quanto accetti il server di posta.",
  "error.mail_utf8_unsupported": "Il server di posta non accetta indirizzi con dieresi o accenti.",
  "error.mail_login_needs_tls": "Un accesso richiede una connessione cifrata (STARTTLS o SSL/TLS).",
  "error.mail_password_required":
    "Per un altro server, un’altra porta o un altro utente, inserisca di nuovo la password.",
  "error.reminder_not_open":
    "La fattura non è più aperta; i suoi solleciti non vengono più inviati.",
  "error.refusedRecipients": "Rifiutati: {refused}.",
  "error.serverSaid": "Risposta del server: «{reply}»",
  "error.not_found": "Non trovato. Potrebbe essere stato eliminato nel frattempo.",
  "error.invalid_state": "Questa azione non è possibile nello stato attuale. Ricarichi la pagina.",
  "error.bank_account_missing":
    "Per questa valuta non è registrato alcun conto bancario. Lo aggiunga in Impostazioni → Conti bancari.",
  "error.bank_account_unknown":
    "Questo conto bancario non esiste più. La preghiamo di sceglierne un altro.",
  "error.bank_account_currency":
    "Il conto bancario è in una valuta diversa da quella del documento.",
  "error.holder_address_missing": "Il conto di terzi richiede l’indirizzo del titolare.",
  "error.no_positions": "Il documento richiede almeno una posizione con un importo.",
  "error.non_positive_total":
    "Il totale di una fattura deve essere maggiore di zero. Le note di credito nascono stornando.",
  "error.due_before_issue": "La data di scadenza è anteriore alla data.",
  "error.reverse_charge_needs_vat_number":
    "Per l’inversione contabile (reverse charge) il cliente necessita di un IDI / n. IVA. Lo aggiunga nel cliente.",
  "error.company_vat_number_missing":
    "Sulla fattura deve figurare il suo n. IVA o IDI. Lo aggiunga in Impostazioni → Azienda oppure scelga lì «Non assoggettato all’IVA / piccola impresa».",
  "error.company_tax_id_missing":
    "Sulla fattura deve figurare il suo numero fiscale o n. di partita IVA UE. Lo aggiunga in Impostazioni → Azienda.",
  "error.company_vat_id_missing":
    "Per una cessione intracomunitaria sulla fattura deve figurare il suo n. IVA o IDI. Lo aggiunga in Impostazioni → Azienda.",
  "error.customer_vat_id_missing":
    "Per una cessione intracomunitaria sulla fattura deve figurare il n. IVA o IDI del cliente. Lo aggiunga nel cliente.",
  "error.goods_only_treatment":
    "La cessione intracomunitaria e l’esportazione valgono solo per le merci. Per il documento scelga «Inversione contabile (reverse charge)» o «Esportazione».",
  "error.customer_vat_number_required":
    "Sulle fatture superiori a 10'000 EUR ad aziende in Austria deve figurare il n. IVA austriaco (UID) del cliente. Lo aggiunga nel cliente.",
  "error.issue_date_in_future": "La data non può essere nel futuro.",
  "error.issue_date_before_last":
    "La data è anteriore all’ultimo documento emesso. Numeri e date devono essere crescenti.",
  "error.payment_exceeds_open_amount": "L’importo è superiore all’importo aperto.",
  "error.payment_date_in_future": "La data di pagamento non può essere nel futuro.",
  "error.write_off_date_in_future":
    "La data della registrazione come perdita non può essere nel futuro.",
  "error.payment_dated_before_write_off":
    "Il pagamento è datato prima della registrazione come perdita. Annulli invece la registrazione come perdita.",
  "error.written_off":
    "La fattura è stata registrata come perdita. Annulli prima la registrazione come perdita.",
  "error.clock_went_back": "L’orologio del server è in ritardo. Riprovi tra un momento.",
  "error.write_off_before_invoice":
    "La data della registrazione come perdita non può essere anteriore alla data della fattura.",
  "error.payment_before_write_off":
    "Questo pagamento è pervenuto prima della registrazione come perdita. Annulli prima la registrazione come perdita.",
  "error.unknown_placeholder":
    "Segnaposto sconosciuto. Sono possibili {nummer}, {datum}, {faellig}, {kunde}, {betrag}, {waehrung}, {firma}, {kontoinhaber} e {kontoinhaber_adresse}.",
  "error.already_converted": "Da questa offerta è già stata creata una fattura.",
  "error.pageDescription":
    "Non è stato possibile visualizzare questa pagina. I Suoi dati salvati non sono interessati.",

  "dashboard.title": "Panoramica",
  "dashboard.welcome": "Buongiorno, {name}",
  "dashboard.emptyTitle": "Ancora nessuna fattura",
  "dashboard.emptyDescription":
    "Non appena emetterà fatture, qui vedrà gli importi aperti, scaduti e pagati.",

  "settings.title": "Impostazioni",
  "settings.account": "Account",
  "settings.security": "Sicurezza",
  "settings.interaction": "Utilizzo",
  "settings.singleKeyShortcuts": "Scorciatoie a tasto singolo",
  "settings.singleKeyShortcutsHint":
    "Scorciatoie come N (nuova fattura) o G poi C (clienti). Le disattivi se usa l’immissione vocale o tecnologie assistive. La tavolozza dei comandi ({mod} K) resta disponibile.",
  "settings.changePassword": "Cambia password",
  "settings.currentPassword": "Password attuale",
  "settings.newPassword": "Nuova password",
  "settings.passwordChanged": "Password modificata. Le altre sessioni sono state disconnesse.",
  "settings.totpTitle": "Autenticazione a due fattori",
  "settings.totpEnabled": "Attivata",
  "settings.totpDisabled": "Non attivata",
  "settings.totpDescription":
    "Protegge ulteriormente il suo account con un codice di un’app di autenticazione.",
  "settings.totpEnable": "Configura",
  "settings.totpDisable": "Disattiva",
  "settings.totpScan":
    "Aggiunga questa chiave alla sua app di autenticazione e poi inserisca il codice visualizzato.",
  "settings.totpSecret": "Chiave",
  "settings.totpConfirm": "Conferma",
  "settings.totpActivated": "L’autenticazione a due fattori è attivata.",
  "settings.totpDeactivated": "L’autenticazione a due fattori è disattivata.",
  "settings.totpDisableConfirm": "Confermi con la sua password e un codice attuale.",

  "common.save": "Salva",
  "common.cancel": "Annulla",
  "common.close": "Chiudi",
  "common.optional": "(facoltativo)",
  "common.copy": "Copia",
  "common.copied": "Copiato",

  "common.new": "Nuovo",
  "common.edit": "Modifica",
  "common.archive": "Archivia",
  "common.restore": "Ripristina",
  "common.archived": "Archiviato",
  "common.delete": "Elimina",
  "common.saved": "Salvato",
  "common.saving": "Salvataggio in corso …",
  "common.back": "Indietro",
  "common.previous": "Pagina precedente",
  "common.next": "Pagina successiva",
  "common.pageInfo": "{from}–{to} di {total}",
  "common.showArchived": "Mostra archiviati",
  "common.undo": "Annulla",
  "common.opensInNewTab": " (si apre in una nuova scheda)",
  "common.loadError": "Non è stato possibile caricare i dati.",
  "common.none": "—",
  "common.default": "Predefinito",
  "common.notSet": "Non impostato",
  "common.useDefault": "Usa predefinito",
  "common.saveShortcut": "Salva ({mod} ↵)",
  "common.unsavedChanges": "Modifiche non salvate",
  "common.results": { zero: "Nessun risultato", one: "1 risultato", other: "{count} risultati" },
  "address.legend": "Indirizzo",
  "address.street": "Via",
  "address.houseNumber": "N.",
  "address.supplement": "Complemento d’indirizzo",
  "address.supplementHint": "ad es. c/o, casella postale o reparto",
  "address.postalCode": "NAP",
  "address.city": "Località",
  "address.country": "Paese",
  "validation.maxLength": "Al massimo {max} caratteri.",
  "validation.number": "Inserisca un numero, ad es. {example}.",
  "validation.range": "Inserisca un numero da {min} a {max}.",
  "validation.decimals": "Al massimo {max} decimali.",
  "validation.percent": "Inserisca un valore tra 0 e 100.",
  "validation.days": "Inserisca un numero intero da 0 a 365.",
  "validation.positiveInteger": "Inserisca un numero intero positivo.",
  "validation.iban.format":
    "Un IBAN inizia con il codice del Paese e due cifre di controllo, ad es. CH93 0076 …",
  "validation.iban.country": "Gli IBAN di questo Paese non sono supportati.",
  "validation.iban.length": "Un IBAN di {country} ha {length} caratteri – lo controlli.",
  "validation.iban.checksum":
    "Le cifre di controllo non corrispondono – verifichi eventuali errori di battitura.",
  "validation.iban.isQr":
    "Questo è un QR-IBAN. Lo inserisca nel campo QR-IBAN e qui l’IBAN normale.",
  "validation.iban.notQr": "Un QR-IBAN ha nelle posizioni 5–9 un numero tra 30000 e 31999.",
  "validation.bic": "Un BIC ha 8 o 11 caratteri, ad es. UBSWCHZH80A.",
  "validation.vat.swiss-format": "Un IDI ha la forma CHE-123.456.789.",
  "validation.vat.swiss-check-digit":
    "La cifra di controllo dell’IDI non è corretta – la controlli.",
  "validation.vat.german-format": "Un n. di partita IVA tedesco ha la forma DE123456789.",
  "validation.vat.austrian-format": "Un n. IVA austriaco (UID) ha la forma ATU12345678.",
  "validation.numberTaken": "Questo numero è già assegnato.",
  "validation.server": "Controlli questa indicazione ({message}).",
  "customers.title": "Clienti",
  "customers.new": "Nuovo cliente",
  "customers.edit": "Modifica cliente",
  "customers.emptyTitle": "Ancora nessun cliente",
  "customers.emptyDescription":
    "Crei il suo primo cliente. L’indirizzo e le condizioni verranno poi ripresi nelle fatture.",
  "customers.noResults": "Nessun cliente per questa ricerca.",
  "customers.resetSearch": "Reimposta ricerca",
  "customers.searchLabel": "Cerca clienti",
  "customers.searchPlaceholder": "Nome, numero, località, e-mail …",
  "customers.number": "N.",
  "customers.numberAndSince": "Numero cliente {number} · cliente dal {since}",
  "customers.numberLabel": "Numero cliente",
  "customers.numberHint": "Lasciare vuoto per il prossimo numero libero.",
  "customers.name": "Nome",
  "customers.city": "Località",
  "customers.email": "E-mail",
  "customers.group": "Gruppo",
  "customers.kind": "Tipo",
  "customers.kindCompany": "Azienda",
  "customers.kindPerson": "Persona privata",
  "customers.sectionBasics": "Dati di base",
  "customers.sectionContact": "Contatto",
  "customers.sectionBilling": "Fatturazione",
  "customers.sectionDelivery": "Indirizzo di consegna",
  "customers.companyName": "Ragione sociale",
  "customers.salutation": "Appellativo",
  "customers.salutationHint": "ad es. Signor, Signora o vuoto",
  "customers.firstName": "Nome",
  "customers.lastName": "Cognome",
  "customers.contactPerson": "Persona di contatto",
  "customers.contactPersonHint": "Appare come «all’att. di» nell’indirizzo.",
  "customers.phone": "Telefono",
  "customers.vatNumber": "IDI / n. IVA",
  "customers.vatNumberHint":
    "Obbligatorio per l’inversione contabile (reverse charge) con clienti aziendali nell’UE.",
  "customers.buyerReference": "Leitweg-ID o riferimento fisso dell’acquirente",
  "customers.buyerReferenceHint":
    "Per la XRechnung. Autorità: la loro Leitweg-ID. Aziende: da compilare solo se desiderano sempre lo stesso riferimento. Se il riferimento cambia a ogni ordine (p. es. il numero d’ordine), lasci vuoto questo campo e lo inserisca nella fattura sotto «Altri dati → Riferimento».",
  "customers.defaultCurrency": "Valuta predefinita",
  "customers.defaultLanguage": "Lingua del documento",
  "customers.taxExempt": "Esente da IVA",
  "customers.taxExemptHint":
    "Solo per clienti esenti dall’imposta, ad es. ambasciate od organizzazioni internazionali. Non per aziende all’estero: un’azienda nell’UE con n. IVA (ad es. ATU12345678) riceve automaticamente l’inversione contabile (reverse charge), un’azienda al di fuori dell’UE automaticamente una fattura senza IVA (esportazione).",
  "customers.taxExemptReverseCharge":
    "Questa azienda ha un n. IVA nell’UE: senza «Esente da IVA» riceve automaticamente l’inversione contabile (reverse charge), cioè l’imposta è dovuta dal destinatario della prestazione. Con «Esente da IVA» sulla fattura figura invece «Prestazione esente dall’imposta sul valore aggiunto».",
  "customers.defaultDiscount": "Sconto predefinito in %",
  "customers.paymentTerms": "Termine di pagamento in giorni",
  "customers.paymentTermsHint": "Lasciare vuoto per il termine di pagamento dell’azienda.",
  "customers.hasDelivery": "Indirizzo di consegna diverso",
  "customers.note": "Nota interna",
  "customers.noteHint": "Non appare sui documenti.",
  "customers.created": "Cliente «{name}» creato.",
  "customers.sheetHint": "La fattura resta aperta dietro. Dopo il salvataggio si continua lì.",
  "customers.createAndChoose": "Crea e scegli",
  "customers.archivedToast": "«{name}» archiviato.",
  "customers.restoredToast": "«{name}» ripristinato.",
  "customers.archivedNotice":
    "Questo cliente è archiviato e non viene mostrato negli elenchi di selezione.",
  "customers.documentsTitle": "Documenti",
  "customers.documentsEmpty": "Per questo cliente non ci sono ancora documenti.",
  "articles.title": "Articoli",
  "articles.new": "Nuovo articolo",
  "articles.edit": "Modifica articolo",
  "articles.emptyTitle": "Ancora nessun articolo",
  "articles.emptyDescription":
    "Registri prodotti e servizi con prezzo e unità per inserirli rapidamente nelle fatture.",
  "articles.noResults": "Nessun articolo per questa ricerca.",
  "articles.searchLabel": "Cerca articoli",
  "articles.searchPlaceholder": "Designazione, numero, descrizione …",
  "articles.number": "N.",
  "articles.numberLabel": "Numero articolo",
  "articles.name": "Designazione",
  "articles.description": "Descrizione",
  "articles.descriptionHint": "Appare sotto la designazione sul documento.",
  "articles.group": "Gruppo",
  "articles.unit": "Unità",
  "articles.taxCategory": "Aliquota IVA",
  "articles.supply": "Bene o servizio",
  "articles.supplyHint":
    "Ai fini fiscali i beni attraversano il confine in modo diverso dai servizi (esportazione, cessione intracomunitaria).",
  "articles.price": "Prezzo",
  "articles.prices": "Prezzi (netti)",
  "articles.pricesHint": "Lasciare vuoto se l’articolo non è offerto in questa valuta.",
  "articles.priceIn": "Prezzo in {currency}",
  "articles.created": "Articolo «{name}» creato.",
  "articles.archivedToast": "«{name}» archiviato.",
  "articles.restoredToast": "«{name}» ripristinato.",
  "articles.archivedNotice":
    "Questo articolo è archiviato e non viene mostrato negli elenchi di selezione.",
  "tax.standard": "Aliquota normale",
  "tax.reduced": "Aliquota ridotta",
  "tax.special": "Aliquota speciale",
  "tax.zero": "0 % (imponibile)",
  "tax.exempt": "Escluso dall’imposta",
  "supply.service": "Servizio",
  "supply.goods": "Bene",
  "settings.tabMail": "E-mail",
  "settings.tabCompany": "Azienda",
  "settings.tabBankAccounts": "Conti bancari",
  "settings.tabNumbering": "Numerazioni",
  "settings.tabTexts": "Testi",
  "texts.intro":
    "I modelli di testo compilano automaticamente i nuovi documenti. I modelli contrassegnati con «Predefinito» vengono inseriti, tutti gli altri può sceglierli nell’editor.",
  "texts.documentType": "Tipo di documento",
  "texts.kind.intro": "Introduzione",
  "texts.kind.payment": "Condizioni di pagamento",
  "texts.kind.closing": "Testo finale",
  "texts.kindHint.intro": "Compare prima delle posizioni, ad es. un ringraziamento per l’ordine.",
  "texts.kindHint.payment":
    "Compare dopo i totali. Più modelli predefiniti vengono disposti uno sotto l’altro.",
  "texts.kindHint.closing": "Compare alla fine del documento, ad es. un saluto.",
  "texts.add": "Aggiungi",
  "texts.addTitle": "Nuovo modello di testo",
  "texts.editTitle": "Modifica modello di testo",
  "texts.empty": "Ancora nessun modello di testo.",
  "texts.default": "Predefinito",
  "texts.moveUp": "Sposta «{name}» in alto",
  "texts.moveDown": "Sposta «{name}» in basso",
  "texts.edit": "Modifica «{name}»",
  "texts.delete": "Elimina «{name}»",
  "texts.deleteTitle": "Eliminare il modello di testo?",
  "texts.deleteDescription":
    "«{name}» verrà eliminato. I documenti che contengono già il testo restano invariati.",
  "texts.deleted": "Modello di testo eliminato",
  "texts.name": "Nome",
  "texts.nameHint": "Breve: appare così nell’editor, ad es. «Pagabile subito».",
  "texts.text": "Testo",
  "texts.placeholders": "Inserisci segnaposto",
  "texts.insertPlaceholder": "Inserisci segnaposto per {placeholder}",
  "texts.placeholder.nummer": "Numero",
  "texts.placeholder.datum": "Data",
  "texts.placeholder.faellig": "Scadenza o validità",
  "texts.placeholder.kunde": "Cliente",
  "texts.placeholder.betrag": "Importo",
  "texts.placeholder.waehrung": "Valuta",
  "texts.placeholder.firma": "La sua azienda",
  "texts.placeholder.kontoinhaber": "Intestatario del conto",
  "texts.placeholder.kontoinhaber_adresse": "Indirizzo dell’intestatario del conto",
  "texts.useAsDefault": "Inserisci nei nuovi documenti (predefinito)",
  "texts.notices": "Indicazioni legali",
  "texts.noticesHint":
    "Compaiono automaticamente in base al caso fiscale. All’emissione il testo viene salvato con il documento. Non costituisce consulenza fiscale: lo faccia verificare.",
  "texts.noticeBuiltIn": "Testo proposto per il suo Paese.",
  "texts.noticeCustomized": "Testo personalizzato.",
  "texts.restoreNotice": "Ripristina proposta",
  "settings.tabSecurity": "Sicurezza",
  "settings.tabInteraction": "Utilizzo",
  "company.title": "Azienda",
  "company.description": "Questi dati compaiono come mittente su tutti i documenti.",
  "company.setupHint": "Inserisca prima i dati della sua azienda.",
  "company.name": "Nome dell’azienda",
  "company.sectionContact": "Contatto",
  "company.sectionTax": "Imposte e registro",
  "company.sectionDefaults": "Impostazioni predefinite",
  "company.sectionAppearance": "Aspetto",
  "company.email": "E-mail",
  "company.phone": "Telefono",
  "company.website": "Sito web",
  "company.vatNumber": "IDI / n. IVA / n. di partita IVA UE",
  "company.vatNumberHint": "ad es. CHE-123.456.789 IVA o DE123456789",
  "company.taxNumber": "Numero fiscale",
  "company.taxNumberHint": "Numero fiscale tedesco (Steuernummer), se non si dispone di un n. IVA.",
  "company.taxDialogTitle": "Dati fiscali dell’azienda",
  "company.taxDialogHint":
    "Questi dati compaiono su ogni fattura e valgono da subito per tutta l’azienda.",
  "company.taxDialogOtherErrors":
    "Altri dati dell’azienda sono incompleti. Verificarli in Impostazioni → Azienda.",
  "company.commercialRegister": "Registro di commercio",
  "company.commercialRegisterHint":
    "ad es. Registro di commercio del Cantone Ticino, CHE-123.456.789",
  "company.smallBusiness": "Non assoggettato all’IVA / piccola impresa",
  "company.smallBusinessHint":
    "Le fatture non contengono allora alcuna imposta, ma l’indicazione richiesta dalla legge.",
  "company.cashRounding": "Arrotondamento ai 5 centesimi",
  "company.cashRoundingHint": "Arrotonda gli importi in CHF a 0.05.",
  "company.defaultCurrency": "Valuta predefinita",
  "company.defaultLanguage": "Lingua dei documenti",
  "company.paymentTerms": "Termine di pagamento in giorni",
  "company.accentHue": "Colore d’accento",
  "company.accentHueHint":
    "Tonalità dell’interfaccia (0–359). Tutte le tonalità sono verificate per la leggibilità.",
  "company.saved": "Dati dell’azienda salvati.",
  "company.logo": "Logo",
  "company.logoHint": "PNG, JPEG o WebP, al massimo 1 MB. Compare in alto sui documenti.",
  "company.logoUpload": "Carica logo",
  "company.logoReplace": "Sostituisci logo",
  "company.logoRemove": "Rimuovi logo",
  "company.logoAlt": "Logo attuale dell’azienda",
  "company.logoUploaded": "Logo salvato.",
  "company.logoRemoved": "Logo rimosso.",
  "company.logoTooLarge": "Il logo può essere al massimo di 1 MB.",
  "language.de": "Tedesco",
  "language.en": "Inglese",
  "language.fr": "Francese",
  "language.it": "Italiano",
  "bank.title": "Conti bancari",
  "bank.description":
    "Conti per i dati di pagamento e i codici QR. Per ogni valuta un conto è quello predefinito; un documento può sceglierne un altro.",
  "bank.add": "Aggiungi conto",
  "bank.edit": "Modifica conto",
  "bank.emptyTitle": "Ancora nessun conto bancario",
  "bank.emptyDescription": "Registri almeno un conto per ogni valuta di fatturazione.",
  "bank.bankName": "Banca",
  "bank.holder": "Titolare del conto",
  "bank.iban": "IBAN",
  "bank.qrIban": "QR-IBAN",
  "bank.qrIbanHint": "Solo per fatture QR con riferimento QR (Svizzera e Liechtenstein).",
  "bank.bic": "BIC / SWIFT",
  "bank.bicEpcHint":
    "Necessario per il codice QR EPC sulle fatture in euro ai clienti nell’UE: senza BIC non viene stampato.",
  "bank.currency": "Valuta",
  "bank.isDefault": "Conto predefinito per questa valuta",
  "bank.thirdParty": "Il conto appartiene a terzi (p. es. un fornitore di servizi finanziari)",
  "bank.thirdPartyHint":
    "I documenti pagati su questo conto indicano il titolare come beneficiario. Il suo indirizzo figura nella sezione pagamento QR.",
  "bank.thirdPartyBadge": "Conto di terzi",
  "bank.holderAddress": "Indirizzo del titolare del conto",
  "bank.saved": "Conto bancario salvato.",
  "bank.deleteTitle": "Eliminare il conto bancario?",
  "bank.deleteDescription":
    "I documenti già emessi mantengono i loro dati del conto. Le bozze e i nuovi documenti useranno il conto predefinito della loro valuta.",
  "bank.deleted": "Conto bancario eliminato.",
  "numbering.title": "Numerazioni",
  "numbering.description":
    "I numeri di fattura vengono assegnati solo all’emissione – senza lacune e univoci.",
  "numbering.kind.invoice": "Fatture",
  "numbering.kind.quote": "Offerte",
  "numbering.kind.cancellation": "Fatture di storno",
  "numbering.kind.customer": "Clienti",
  "numbering.kind.article": "Articoli",
  "numbering.pattern": "Schema",
  "numbering.patternHint":
    "{YYYY} anno, {YY} anno a due cifre, {MM} mese, {NNNN} contatore (numero di N = cifre minime).",
  "numbering.nextValue": "Prossimo contatore",
  "numbering.nextValueHint": "Per proseguire senza interruzioni da un programma precedente.",
  "numbering.startValue": "Valore iniziale dopo il cambio d’anno",
  "numbering.yearlyReset": "Azzera ogni anno",
  "numbering.preview": "Prossimo numero",
  "numbering.saved": "Numerazione salvata.",
  "numbering.problem.length": "Lo schema deve contenere da 1 a 40 caratteri.",
  "numbering.problem.placeholder":
    "Segnaposto sconosciuto. Sono ammessi {YYYY}, {YY}, {MM} e {N…}.",
  "numbering.problem.counter": "Lo schema richiede esattamente un contatore come {NNNN}.",
  "numbering.problem.characters": "Sono ammessi lettere, cifre e - _ / . oltre ai segnaposto.",
  "numbering.problem.year":
    "Con l’azzeramento annuale lo schema richiede {YYYY} o {YY}, altrimenti i numeri si ripetono.",
  "shortcuts.newQuote": "Nuova offerta",
  "shortcuts.newCustomer": "Nuovo cliente",
  "shortcuts.newArticle": "Nuovo articolo",
  "shortcuts.or": "o",
  "shortcuts.undo": "Annulla l’ultima azione (finché il messaggio è visibile)",
  "shortcuts.payment": "Registra pagamento (nel documento)",
  "shortcuts.moveLineUp": "Sposta la posizione in alto (nell’editor)",
  "shortcuts.moveLineDown": "Sposta la posizione in basso (nell’editor)",
  "shortcuts.goQuotes": "Vai alle offerte",

  "dashboard.asTable": "Come tabella",
  "dashboard.currency": "Valuta",
  "dashboard.revenueYear": "Fatturato {year}",
  "dashboard.vsLastYear": "rispetto allo stesso periodo dell’anno precedente",
  "dashboard.noComparison": "nessun anno precedente",
  "dashboard.showInvoices": "Vedi fatture",
  "dashboard.showOpen": "Fatture aperte",
  "dashboard.showDunning": "Vai ai solleciti",
  "dashboard.showQuotes": "Vedi offerte aperte",
  "dashboard.daysToPay": {
    zero: "I clienti pagano lo stesso giorno",
    one: "Ø pagata dopo 1 giorno",
    other: "Ø pagata dopo {count} giorni",
  },
  "dashboard.oldestOverdue": {
    zero: "in scadenza oggi",
    one: "la più vecchia da 1 giorno",
    other: "la più vecchia da {count} giorni",
  },
  "dashboard.openQuotes": "Offerte aperte",
  "dashboard.quoteCount": { zero: "Nessuna offerta", one: "1 offerta", other: "{count} offerte" },
  "dashboard.acceptance": "Tasso di accettazione {rate} quest’anno",
  "dashboard.noDecisions": "Ancora nessuna offerta decisa",
  "dashboard.aging": "Aperto per scadenza",
  "dashboard.nothingOpen": "Nessuna fattura aperta.",
  "dashboard.age.not_due": "Non ancora in scadenza",
  "dashboard.age.late_30": "Scadute da 1–30 giorni",
  "dashboard.age.late_60": "Scadute da 31–60 giorni",
  "dashboard.age.late_more": "Scadute da oltre 60 giorni",
  "dashboard.overdueList": "Scadute da più tempo",
  "dashboard.noOverdue": "Nessuna fattura scaduta.",
  "dashboard.daysLate": {
    zero: "in scadenza oggi",
    one: "scaduta da 1 giorno",
    other: "scaduta da {count} giorni",
  },
  "dashboard.remind": "Vai ai solleciti",
  "dashboard.topCustomers": "Migliori clienti {year}",
  "dashboard.topCustomersHint": "Fatturato netto, dedotti gli storni.",
  "dashboard.noCustomerRevenue": "Ancora nessun fatturato quest’anno.",
  "dashboard.expected": "Pagamenti attesi",
  "dashboard.expectedHint":
    "Fatture aperte per settimana di scadenza; questa settimana include tutto ciò che è scaduto.",
  "dashboard.week": "Settimana",
  "dashboard.weekLabel": "Sett. {week}",
  "dashboard.amount": "Importo",
  "dashboard.figuresIn": "Indicatori in {currency}",
  "dashboard.open": "Aperto",
  "dashboard.openCount": {
    zero: "Nessuna fattura aperta",
    one: "1 fattura aperta",
    other: "{count} fatture aperte",
  },
  "dashboard.overdue": "Scaduto",
  "dashboard.overdueCount": {
    zero: "Niente di scaduto",
    one: "1 fattura scaduta",
    other: "{count} fatture scadute",
  },
  "dashboard.paid": "Pagato",
  "dashboard.paidHint": "Somma di tutti i pagamenti ricevuti",
  "dashboard.revenue": "Fatturato mensile",
  "dashboard.revenueHint": "Fatturato netto in {currency}, dedotti gli storni.",
  "dashboard.revenueIn": "Fatturato in {currency}",
  "dashboard.noRevenue": "Negli ultimi 12 mesi non sono state emesse fatture.",
  "dashboard.month": "Mese",
  "dashboard.netRevenue": "Fatturato netto",
  "dashboard.activity": "Attività recenti",
  "dashboard.noActivity": "Ancora nessuna attività.",

  "documentType.invoice": "Fattura",
  "documentType.quote": "Offerta",
  "documentType.cancellation": "Fattura di storno",

  "status.draft": "Bozza",
  "status.sent": "Inviata",
  "status.accepted": "Accettata",
  "status.declined": "Rifiutata",
  "status.expired": "Scaduta",
  "status.issued": "Emessa",
  "status.partially_paid": "Parzialmente pagata",
  "status.overdue": "Scaduta",
  "status.overdueDays": {
    zero: "Scaduta",
    one: "Scaduta · 1 giorno",
    other: "Scaduta · {count} giorni",
  },
  "status.paid": "Pagata",
  "status.cancelled": "Stornata",
  "status.written_off": "Registrata come perdita",

  "event.emailed": "Inviato per e-mail",
  "event.reminder_emailed": "Sollecito inviato per e-mail",
  "event.created": "Creato",
  "event.created_from": "Creato da",
  "event.issued": "Emesso",
  "event.sent": "Inviato",
  "event.accepted": "Accettato",
  "event.declined": "Rifiutato",
  "event.converted": "Convertito in fattura",
  "event.payment_added": "Pagamento registrato",
  "event.payment_removed": "Pagamento rimosso",
  "event.cancelled": "Stornato",
  "event.tax_overridden": "Caso fiscale scelto manualmente",
  "event.reminded": "Sollecito redatto",
  "event.imported": "Ripreso dal programma precedente",
  "event.file_added": "File aggiunto",
  "event.written_off": "Registrato come perdita",
  "event.write_off_removed": "Registrazione come perdita annullata",

  "treatment.domestic": "Nazionale (imponibile)",
  "treatment.reverse_charge": "Inversione contabile (reverse charge)",
  "treatment.export": "Esportazione (esente da imposta)",
  "treatment.small_business": "Piccola impresa (senza imposta)",
  "treatment.exempt": "Esente da imposta",
  "treatment.intra_community": "Cessione intracomunitaria (esente da imposta)",
  "treatment.goods_export": "Cessione all’esportazione (esente da imposta)",

  "documents.newInvoice": "Nuova fattura",
  "documents.newQuote": "Nuova offerta",
  "documents.draftTitle": "{kind} (bozza)",
  "documents.numberedTitle": "{kind} {number}",
  "documents.searchLabel": "Cerca nei documenti",
  "documents.searchPlaceholder": "Numero, cliente, oggetto, posizione …",
  "documents.views": "Vista",
  "documents.view.all": "Tutti",
  "documents.view.draft": "Bozze",
  "documents.view.open": "Aperti",
  "documents.view.overdue": "Scaduti",
  "documents.view.paid": "Pagati",
  "documents.view.cancelled": "Stornati",
  "documents.view.written_off": "Registrati come perdita",
  "documents.view.sent": "Inviati",
  "documents.view.accepted": "Accettati",
  "documents.view.declined": "Rifiutati",
  "documents.view.expired": "Scaduti",
  "documents.noResults": "Nessun documento per questo filtro.",
  "documents.resetFilter": "Reimposta filtro",
  "documents.emptyInvoices": "Ancora nessuna fattura",
  "documents.emptyInvoicesDescription":
    "Crei la sua prima fattura. Il numero viene assegnato solo all’emissione.",
  "documents.emptyQuotes": "Ancora nessuna offerta",
  "documents.emptyQuotesDescription":
    "Crei un’offerta e, una volta accettata, la converta in fattura con un clic.",
  "documents.number": "Numero",
  "documents.status": "Stato",
  "documents.amount": "Importo",
  "documents.openShort": "aperto {amount}",
  "documents.applyLine": "Applica",
  "documents.customerNumber": "N. cliente {number}",
  "documents.customerSearch": "Cerca cliente",
  "documents.changeCustomer": "Cambia",
  "documents.discardLine": "Scarta",
  "documents.saveAsArticle": "Salva come articolo",
  "documents.articleSaved": "Articolo {number} «{name}» salvato.",
  "documents.dueInDays": "{days} giorni",
  "documents.dueNow": "Subito",
  "documents.editLine": "Modifica posizione {number}: {description}",
  "documents.editPane": "Modifica",
  "documents.insertTemplate": "Inserisci modello di testo …",
  "documents.linePriceIn": "Prezzo {currency}",
  "documents.manageTemplates": "Gestisci modelli di testo",
  "documents.netTotal": "Netto {amount}",
  "documents.noLines":
    "Ancora nessuna posizione. Ne aggiunga una; durante la digitazione vengono proposti articoli.",
  "documents.noticeAuto": "Indicazione automatica:",
  "documents.paneSwitch": "Vista",
  "documents.paymentTemplates": "Condizioni di pagamento dai modelli di testo",
  "documents.paymentText": "Condizioni di pagamento",
  "documents.payWithin": "Termine di pagamento",
  "documents.placeholdersHint":
    "I segnaposto per numero, scadenza, cliente e importo vengono sostituiti in fase di stampa.",
  "documents.previewHint":
    "L’anteprima mostra il documento come stampato. Un clic su una posizione la apre nell’elenco.",
  "documents.previewPane": "Anteprima",
  "documents.cannotMarkPaid":
    "{number} non può essere contrassegnata come pagata (nel frattempo pagata, stornata o una bozza). Non è stato modificato nulla.",
  "documents.popupBlocked":
    "Il browser ha bloccato la nuova finestra. Consenta i pop-up per Rhinvoice oppure usi «Scarica».",
  "error.unavailable":
    "Il servizio PDF non è raggiungibile al momento. Riprovi più tardi o si rivolga alla persona che gestisce Rhinvoice.",
  "error.cannot_mark_paid":
    "Una delle fatture selezionate non può essere contrassegnata come pagata (nel frattempo pagata o stornata). Non è stato modificato nulla.",
  "documents.autosaveFailed": "Salvataggio automatico non riuscito. Salvi manualmente.",
  "documents.pdf": "PDF",
  "documents.download": "Scarica",
  "documents.print": "Stampa",
  "documents.share": "Condividi",
  "documents.viewMode": "Visualizzazione",
  "documents.viewDocument": "Documento",
  "documents.viewDetails": "Dettagli",
  "documents.imported": "Ripreso",
  "documents.importedHint":
    "Ripreso da un programma precedente. Il PDF costituisce la fattura; non ci sono posizioni.",
  "documents.originalPdf": "PDF originale",
  "documents.noPdf": "Ancora nessun PDF",
  "documents.noPdfHint":
    "Durante la ripresa non è stato trovato alcun PDF per questa fattura. Lo aggiunga affinché sia completa. In seguito non potrà più essere sostituito.",
  "documents.addPdf": "Aggiungi PDF",
  "documents.attachmentsTitle": "Allegati",
  "documents.attachmentsHint": "Solleciti, correzioni e copie dal programma precedente.",
  "documents.attachmentsEmpty": "Nessun allegato.",
  "documents.addPdfTitle": "Aggiungere il PDF?",
  "documents.addAttachmentTitle": "Allegare il file?",
  "documents.addFileConfirm":
    "«{name}» viene conservato in modo permanente con questa fattura. In seguito non potrà più essere né sostituito né rimosso.",
  "documents.addAttachment": "Allega file",
  "documents.fileAdded": "File aggiunto.",
  "documents.pdfAdded": "PDF aggiunto.",
  "documents.addFileHint":
    "Solo PDF, al massimo 20 MB. I file aggiunti non possono più essere rimossi.",
  "documents.paidOnUnknown":
    "Data di pagamento non nota dal programma precedente; ripresa come data di scadenza.",
  "documents.pdfLoading": "Caricamento del PDF …",
  "documents.previewEmpty":
    "L’anteprima appare non appena sono registrati un cliente e una posizione.",
  "documents.previewTitle": "Anteprima: {title}",
  "documents.reverseChargeHint":
    "Inversione contabile (reverse charge): nessuna {tax}. L’indicazione e l’IDI del cliente vengono riportate automaticamente sul documento.",
  "documents.sectionMore": "Altri dati",
  "documents.sectionMoreHint": "Oggetto, riferimento, periodo della prestazione, valuta, sconto",
  "documents.sectionTexts": "Testi",
  "documents.sectionTextsHint": "Introduzione, condizioni di pagamento, chiusura",
  "documents.filters": "Filtri",
  "documents.year": "Anno",
  "documents.allYears": "Tutti gli anni",
  "documents.customerFilter": "Cliente",
  "documents.allCustomers": "Tutti i clienti",
  "documents.removeCustomerFilter": "Rimuovi filtro cliente",
  "documents.selectDocument": "Seleziona {number}",
  "documents.selectAllPayable": "Seleziona tutte le fatture aperte di questa pagina",
  "documents.markPaidShort": "Pagata",
  "documents.markPaidRow": "Contrassegna {number} come pagata (oggi, importo intero)",
  "documents.selection": "Selezione",
  "documents.selectedCount": { one: "1 selezionato", other: "{count} selezionati" },
  "documents.markSelectedPaid": "Contrassegna come pagate",
  "documents.clearSelection": "Annulla selezione",
  "documents.markedPaidNumber": "{number} contrassegnata come pagata",
  "documents.markedPaidMany": "{count} fatture contrassegnate come pagate",
  "documents.paymentsUndone": "Pagamenti rimossi",
  "documents.sums": "Totali",
  "documents.sumCountInvoices": {
    one: "1 fattura in {currency}",
    other: "{count} fatture in {currency}",
  },
  "documents.sumCountQuotes": {
    one: "1 offerta in {currency}",
    other: "{count} offerte in {currency}",
  },
  "documents.sumTotal": "Totale {amount}",
  "documents.sumOpen": "di cui aperto {amount}",
  "documents.companyMissingTitle": "Mancano i dati dell’azienda",
  "documents.companyMissing":
    "Inserisca prima i dati della sua azienda. Compaiono come mittente su ogni documento.",
  "documents.facts": "Dati principali",
  "documents.sectionRecipient": "Destinatario e oggetto",
  "documents.customer": "Cliente",
  "documents.customerPlaceholder": "Inserire nome, numero o località …",
  "documents.customerRequired": "Scelga un cliente.",
  "documents.noCustomerFound": "Nessun cliente trovato.",
  "documents.createCustomerNamed": "Crea «{name}» come nuovo cliente",
  "documents.noCustomersTitle": "Ancora nessun cliente.",
  "documents.noCustomersHint":
    "Una fattura ha bisogno di un destinatario. Crearlo qui, la fattura resta aperta.",
  "documents.createCustomer": "Crea cliente",
  "documents.importCustomers": "Importa clienti",
  "documents.blockersTitle": "Manca ancora prima dell’emissione:",
  "documents.blockersHint": "Il salvataggio funziona già; solo l’emissione attende questi dati.",
  "blocker.bank_account_missing": "un conto bancario in {currency} per i dati di pagamento",
  "blocker.reverse_charge_needs_vat_number": "il numero IVA del cliente per l’inversione contabile",
  "blocker.company_vat_number_missing":
    "il vostro numero IVA, oppure l’indicazione «non assoggettato all’IVA»",
  "blocker.company_tax_id_missing": "il vostro numero fiscale o numero IVA",
  "blocker.customer_vat_number_required": "il numero IVA del cliente (fatture oltre 10.000 EUR)",
  "blocker.company_vat_id_missing": "il vostro numero IVA per la cessione intracomunitaria",
  "blocker.customer_vat_id_missing": "il numero IVA del cliente per la cessione intracomunitaria",
  "blocker.fix.bank_account": "Aggiungi conto {currency}",
  "blocker.fix.company": "Completa i dati fiscali",
  "blocker.fix.customer": "Completa il cliente",
  "documents.subject": "Oggetto",
  "documents.subjectHint": "p. es. «Rifacimento del sito web, fase 2»",
  "documents.reference": "Riferimento",
  "documents.referenceHint": "p. es. numero d’ordine del cliente",
  "documents.referenceHintXRechnung": "Numero d’ordine o simile – necessario per la XRechnung",
  "documents.referenceHintFixed": "La XRechnung usa la Leitweg-ID del cliente: {value}",
  "documents.currency": "Valuta",
  "documents.language": "Lingua",
  "documents.sectionDates": "Date",
  "documents.issueDate": "Data",
  "documents.issueDateHint":
    "Precompilata con la data di oggi. Lasciare vuoto per usare la data di emissione.",
  "documents.dueDate": "Scadenza il",
  "documents.bankAccount": "Coordinate bancarie",
  "documents.bankAccountHint": "Il conto su cui viene pagato questo documento.",
  "documents.bankAccountDefault": "(predefinito)",
  "documents.bankAccountThirdParty": "(conto di terzi)",
  "documents.dueDateHint":
    "Lasciare vuoto per usare il termine di pagamento del cliente o dell’azienda.",
  "documents.validUntil": "Valida fino al",
  "documents.validUntilHint": "Lasciare vuoto per 30 giorni dalla data.",
  "documents.serviceFrom": "Prestazione dal",
  "documents.serviceTo": "Prestazione fino al",
  "documents.serviceHint": "Determinante per l’aliquota; se manca, vale la data.",
  "documents.servicePeriod": "Periodo della prestazione",
  "documents.dueBeforeIssue": "La data di scadenza è anteriore alla data.",
  "documents.serviceEndsBeforeStart": "Il periodo della prestazione termina prima del suo inizio.",
  "documents.introText": "Testo introduttivo",
  "documents.closingText": "Testo finale",
  "documents.closingTextHint":
    "p. es. ringraziamenti, indicazioni di pagamento o validità dell’offerta.",

  "documents.sectionLines": "Posizioni",
  "documents.priceMode": "Prezzi",
  "documents.priceModeNet": "Netto (più {tax})",
  "documents.priceModeGross": "Lordo ({tax} inclusa)",
  "documents.lineLabel": "Posizione {number}",
  "documents.lineError": "Posizione {number}: {message}",
  "documents.lineDescription": "Descrizione",
  "documents.lineText": "Testo",
  "documents.lineTextPlaceholder": "Titolo intermedio o nota",
  "documents.lineDetails": "Testo aggiuntivo della posizione {number}",
  "documents.lineDetailsPlaceholder": "Testo aggiuntivo (appare sotto la descrizione)",
  "documents.addDetails": "Aggiungi testo aggiuntivo",
  "documents.lineQuantity": "Quantità",
  "documents.lineUnit": "Unità",
  "documents.linePrice": "Prezzo",
  "documents.lineDiscount": "Sconto %",
  "documents.lineDiscountApplied": "dedotto uno sconto del {percent}",
  "documents.lineTax": "Aliquota",
  "documents.lineSupply": "Merce o servizio",
  "documents.goodsTreatment": "Su questo documento: {treatment}",
  "documents.intraCommunityHint":
    "La merce costituisce una cessione intracomunitaria esente da imposta. Conservi la prova che è arrivata nell’altro Paese UE (p. es. attestazione di arrivo o prova di spedizione) e dichiari la cessione nell’elenco riepilogativo.",
  "documents.goodsExportHint":
    "La merce costituisce una cessione all’esportazione esente da imposta. È esente solo con la prova dell’esportazione (documento doganale); la conservi.",
  "documents.lineTaxRate": "Aliquota {tax}",
  "documents.lineAmount": "Importo",
  "documents.lineActions": "Azioni",
  "documents.addLine": "Aggiungi posizione",
  "documents.addTextLine": "Aggiungi riga di testo",
  "documents.moveUp": "Sposta in alto la posizione {number}",
  "documents.moveDown": "Sposta in basso la posizione {number}",
  "documents.removeLine": "Rimuovi la posizione {number}",
  "documents.tooManyLines": "Un documento ha al massimo {max} posizioni.",

  "documents.sectionConditions": "Sconto e imposta",
  "documents.discountKind": "Sconto globale",
  "documents.discountPercent": "in percentuale",
  "documents.discountAmount": "come importo in {currency}",
  "documents.discountValuePercent": "Sconto in %",
  "documents.discountValueAmount": "Sconto in {currency}",
  "documents.treatment": "Regime fiscale",
  "documents.treatmentHint":
    "Viene determinato dal Paese dell’azienda e da quello del cliente. Una scelta manuale viene registrata.",
  "documents.treatmentAuto": "Automatico",
  "documents.treatmentAutoWith": "Automatico: {treatment}",
  "documents.overrideReason": "Motivazione",
  "documents.overrideReasonHint":
    "Perché il regime fiscale è diverso? Viene salvato nella cronologia.",
  "documents.overrideReasonRequired": "Motivi la scelta manuale.",

  "documents.totals": "Totali",
  "documents.totalsPending": "I totali appaiono non appena tutti i dati sono validi.",
  "documents.subtotal": "Subtotale",
  "documents.documentDiscount": "Sconto",
  "documents.net": "Netto",
  "documents.taxGroup": "{tax} {rate} su {amount}",
  "documents.rounding": "Arrotondamento",
  "documents.total": "Totale",
  "documents.paid": "Pagato",
  "documents.openAmount": "Aperto",

  "documents.issue": "Emetti",
  "documents.issueTitle": "Emettere la fattura?",
  "documents.issueDescription":
    "La fattura riceve il suo numero definitivo e in seguito non può più essere modificata. Le correzioni sono possibili solo con una fattura di storno.",
  "documents.send": "Segna come inviata",
  "documents.sendTitle": "Segnare l’offerta come inviata?",
  "documents.sendDescription":
    "L’offerta riceve il suo numero e in seguito non può più essere modificata. Per le modifiche occorre una nuova offerta (Duplica).",
  "documents.issuedToast": "Fattura {number} emessa.",
  "documents.sentToast": "Offerta {number} segnata come inviata.",
  "documents.draftSaved": "Bozza salvata.",
  "documents.leaveConfirm": "Ci sono modifiche non salvate. Lasciare comunque la pagina?",
  "documents.moreActions": "Altre azioni",
  "documents.duplicate": "Duplica",
  "documents.duplicatedToast": "Copia creata come bozza.",
  "documents.convert": "Converti in fattura",
  "documents.convertedToast": "Bozza di fattura creata dall’offerta.",
  "documents.accept": "Accettata",
  "documents.acceptedToast": "Offerta segnata come accettata.",
  "documents.decline": "Rifiutata",
  "documents.declinedToast": "Offerta segnata come rifiutata.",
  "documents.cancel": "Storna",
  "documents.cancelTitle": "Stornare la fattura?",
  "documents.cancelDescription":
    "Viene emessa una fattura di storno con un proprio numero, che annulla questa fattura. L’operazione non può essere annullata.",
  "documents.cancelledToast": "Fattura stornata, fattura di storno emessa.",
  "documents.deleteDraft": "Elimina bozza",
  "documents.deleteTitle": "Eliminare la bozza?",
  "documents.deleteDescription":
    "La bozza viene eliminata definitivamente. I documenti emessi vengono sempre conservati.",
  "documents.deletedToast": "Bozza eliminata.",
  "documents.openCancellation": "Apri fattura di storno",
  "documents.openCancelledInvoice": "Apri fattura stornata",
  "documents.openSource": "Apri documento d’origine",
  "documents.openInvoice": "Apri bozza di fattura",
  "documents.openConvertedInvoice": "Apri fattura",
  "documents.history": "Cronologia",

  "documents.customerVatMissing":
    "Il cliente è un’impresa nell’UE, ma non ha un IDI / n. IVA. Con il numero varrebbe: {withNumber}; senza: {treatment}.",
  "documents.addCustomerVat": "Registra l’IDI / n. IVA del cliente",
  "documents.markPaid": "Segna come pagata",
  "documents.markedPaid": "Segnata come pagata.",
  "documents.addPayment": "Registra pagamento",
  "documents.addPaymentDescription":
    "Importo aperto: {amount}. Per un pagamento parziale, modificare l’importo.",
  "documents.paidOn": "Data di pagamento",
  "documents.paymentAmount": "Importo in {currency}",
  "documents.paymentNote": "Nota",
  "documents.recordPayment": "Salva pagamento",
  "documents.paymentTooHigh": "L’importo è superiore all’importo aperto di {amount}.",
  "documents.paymentAdded": "Pagamento di {amount} registrato.",
  "documents.paymentRemoved": "Pagamento rimosso.",
  "documents.payments": "Pagamenti",
  "documents.removePayment": "Rimuovi il pagamento di {amount} del {date}",

  "writeOff.action": "Registra come perdita …",
  "writeOff.title": "Registrare la fattura {number} come perdita",
  "writeOff.description":
    "La fattura rimane invariata e il cliente non riceve nulla. Non è più considerata aperta e non viene più sollecitata. Se il cliente paga in seguito, registri il pagamento come di consueto.",
  "writeOff.openAmount": "Importo aperto",
  "writeOff.date": "Registrata come perdita al",
  "writeOff.today": "Oggi",
  "writeOff.endOfInvoiceYear": "Fine dell’anno della fattura",
  "writeOff.dateRange": "La data è compresa tra la data della fattura e oggi.",
  "writeOff.reason": "Motivo",
  "writeOff.reason.gone": "Ditta cancellata o fallita",
  "writeOff.reason.time_barred": "Prescritta",
  "writeOff.reason.small_amount": "Importo esiguo",
  "writeOff.reason.untraceable": "Irreperibile",
  "writeOff.reason.other": "Altro motivo",
  "writeOff.note": "Nota",
  "writeOff.submit": "Registra come perdita",
  "writeOff.done": "Fattura registrata come perdita.",
  "writeOff.remove": "Revoca la perdita",
  "writeOff.removeTitle": "Revocare la perdita?",
  "writeOff.removeDescription":
    "L’importo registrato come perdita torna aperto e può essere sollecitato. Ciò viene annotato nella cronologia.",
  "writeOff.removed": "Perdita revocata.",
  "writeOff.card": "Perdita su debitori",
  "writeOff.amount": "Registrato come perdita",
  "writeOff.writtenOffOn": "Al",
  "writeOff.taxIncluded": "{tax} inclusa",
  "writeOff.recovered": "Pagato in seguito",
  "writeOff.remaining": "Perdita",
  "writeOff.afterWriteOff": "Ricevuto dopo la registrazione come perdita",
  "writeOff.paymentDescription":
    "Registrato come perdita: {amount}. Il pagamento riduce la perdita di questo importo.",
  "badDebts.description":
    "Le perdite contano nell’anno della loro data, i pagamenti successivi nell’anno in cui sono stati ricevuti.",
  "badDebts.year": "Anno",
  "badDebts.writtenOff": "Registrato come perdita",
  "badDebts.recovered": "Pagato in seguito",
  "badDebts.loss": "Perdita",
  "badDebts.net": "Netto",
  "settings.tabBackup": "Backup",
  "backup.title": "Backup",
  "backup.description":
    "La banca dati viene salvata in forma cifrata: ogni giorno e poco dopo ogni emissione o storno.",
  "backup.stateTitle": "Stato",
  "backup.state.current": "Aggiornato",
  "backup.state.stale": "Non aggiornato",
  "backup.state.not_set_up": "Non configurato",
  "backup.notSetUp":
    "I backup non sono configurati. Se il server si guasta, tutte le fatture vanno perse.",
  "backup.setUpHint":
    "La destinazione del backup (p. es. Dropbox, Google Drive, S3 o un NAS) viene definita durante l’installazione nel file .env.",
  "backup.guide": "Apri la guida",
  "backup.never": "Ancora nessun backup riuscito.",
  "backup.lastSuccess": "Ultimo backup riuscito {when}.",
  "backup.lastFailure": "Ultimo tentativo non riuscito: {message}",
  "backup.show": "Mostra backup",
  "backup.check": "Verifica backup",
  "backup.now": "Esegui backup ora",
  "backup.requested": "Backup richiesto.",
  "backup.pending": "Richiesto, inizia a breve.",
  "backup.running": "Backup in corso …",
  "backup.unknown": "Al momento non è possibile recuperare lo stato dei backup.",
  "backup.runs": "Ultimi backup",
  "backup.startedAt": "Momento",
  "backup.reasonTitle": "Motivo",
  "backup.result": "Risultato",
  "backup.added": "Nuovi dati salvati",
  "backup.reason.daily": "Giornaliero",
  "backup.reason.document": "Dopo emissione o storno",
  "backup.reason.manual": "Manuale",
  "backup.status.running": "In corso …",
  "backup.status.succeeded": "Riuscito",
  "backup.status.failed": "Non riuscito",
  "backup.status.not_configured": "Non configurato",
  "backup.retention":
    "Vengono conservati 7 backup giornalieri, 4 settimanali, 12 mensili e tutti quelli annuali. Il ripristino è descritto nella guida.",
  "error.backup_not_set_up": "I backup non sono configurati.",
  "error.backup_folder_unusable": "Questa cartella non può essere utilizzata.",
  "error.backup_by_container":
    "Qui i backup vengono configurati nel container di backup (vedi guida).",
  "backup.folder": "I backup vengono salvati in {folder}.",
  "backup.changeFolder": "Cambia cartella",
  "backup.firstBackup":
    "Il primo backup inizia a breve, non appena i dati dell’azienda sono configurati (Impostazioni → Azienda). In seguito può eseguire un backup qui in qualsiasi momento.",
  "backup.folderUnusable": "Questa cartella non può essere utilizzata: {reason}",
  "backup.setup.title": "Configura backup",
  "backup.setup.changeTitle": "Scegli un’altra cartella",
  "backup.setup.description":
    "Idealmente in una cartella sincronizzata da un servizio cloud. Così una copia si trova al di fuori di questo computer.",
  "backup.setup.folders": "Cartella per i backup",
  "backup.setup.found": "trovata",
  "backup.setup.other": "Altra cartella …",
  "backup.setup.otherHint": "Per esempio un’unità di rete o un disco rigido USB.",
  "backup.setup.path": "Percorso della cartella",
  "backup.setup.pathHint":
    "Il percorso completo, p. es. E:\\Rhinvoice-Backup o /Volumes/Backup/Rhinvoice. Se la cartella non esiste, viene creata.",
  "backup.setup.pathMissing": "Indichi il percorso di una cartella.",
  "backup.setup.continue": "Avanti alla chiave",
  "backup.setup.change": "Usa questa cartella",
  "backup.setup.changed": "I prossimi backup vengono salvati nella nuova cartella.",
  "backup.key.title": "La sua chiave di ripristino",
  "backup.key.description":
    "Cifra i backup. Senza di essa nessun backup può essere ripristinato. La conservi separata da questo computer, in un gestore di password o stampata.",
  "backup.key.label": "Chiave di ripristino",
  "backup.key.kept": "Ho conservato la chiave in un luogo sicuro.",
  "backup.key.finish": "Configura backup",
  "backup.key.done": "Backup configurato. Il primo inizia a breve.",
  "backup.key.show": "Mostra chiave",
  "backup.key.hide": "Nascondi chiave",
  "import.title.customers": "Importa clienti",
  "import.title.articles": "Importa articoli",
  "import.description":
    "Scegliere il file, controllare l’associazione, esaminare il risultato. Solo «Importa» salva i dati.",
  "import.chooseFile": "Scegli file",
  "import.otherFile": "Altro file",
  "import.resultRows": "Righe del file",
  "import.missingColumns": "Associare le colonne: manca una colonna per {columns}.",
  "import.or": " o ",
  "import.unnamedColumn": "Colonna {number}",
  "import.deleteTemplateTitle": "Eliminare il modello?",
  "import.deleteTemplateDescription":
    "Il modello «{name}» viene eliminato. I dati importati vengono mantenuti.",
  "import.problem.unknown_country_prefix": "sigla del Paese sconosciuta prima del NAP",
  "import.open": "Importa",
  "import.run.customers": {
    zero: "Nessun cliente da importare",
    one: "Importa 1 cliente",
    other: "Importa {count} clienti",
  },
  "import.run.articles": {
    zero: "Nessun articolo da importare",
    one: "Importa 1 articolo",
    other: "Importa {count} articoli",
  },
  "import.done.customers": {
    zero: "Nessun cliente importato.",
    one: "1 cliente importato.",
    other: "{count} clienti importati.",
  },
  "import.done.articles": {
    zero: "Nessun articolo importato.",
    one: "1 articolo importato.",
    other: "{count} articoli importati.",
  },
  "import.choose.customers": "File CSV o Excel con clienti",
  "import.choose.articles": "File CSV o Excel con articoli",
  "import.formats":
    "CSV (UTF-8 o Windows-1252), Excel .xlsx e .xls, al massimo 5 MB e {rows} righe.",
  "import.rows": { zero: "nessuna riga", one: "1 riga", other: "{count} righe" },
  "import.settings": "Impostazioni",
  "import.template": "Modello",
  "import.templateHint":
    "Un’associazione delle colonne salvata, p. es. per un programma usato in precedenza.",
  "import.templateAuto": "Riconosciuto automaticamente",
  "import.saveTemplate": "Salva come modello",
  "import.saveTemplateHint":
    "L’associazione viene salvata con questo nome e si applica ai file con gli stessi nomi di colonna.",
  "import.templateName": "Nome del modello",
  "import.templateSaved": "Modello salvato.",
  "import.templateDeleted": "Modello eliminato.",
  "import.deleteTemplate": "Elimina il modello «{name}»",
  "import.duplicates": "Se la voce esiste già",
  "import.duplicates.skip": "Salta",
  "import.duplicates.update": "Aggiorna",
  "import.duplicates.create": "Crea nuova",
  "import.duplicatesHint.customers":
    "Uguale significa: stesso numero cliente; senza numero, stesso nome e stesso NAP. Durante l’aggiornamento le celle vuote non hanno effetto.",
  "import.duplicatesHint.articles":
    "Uguale significa: stesso numero articolo; senza numero, stessa descrizione. Durante l’aggiornamento le celle vuote non hanno effetto.",
  "import.mapping": "Associa le colonne ({mapped} di {total} associate)",
  "import.required.customers":
    "È necessaria una colonna per il numero cliente o il nome (azienda o cognome). I nuovi clienti richiedono inoltre NAP e località.",
  "import.required.articles":
    "È necessaria una colonna per il numero articolo o la descrizione. I nuovi articoli richiedono una descrizione.",
  "import.mappingCaption": "Associazione delle colonne",
  "import.column": "Colonna nel file",
  "import.example": "Esempio",
  "import.becomes": "Diventa",
  "import.fieldFor": "Campo per la colonna {column}",
  "import.skipColumn": "Non importare",
  "import.field.number": "Numero",
  "import.field.company_name": "Nome dell’azienda",
  "import.field.salutation": "Appellativo",
  "import.field.first_name": "Nome",
  "import.field.last_name": "Cognome",
  "import.field.contact_person": "Persona di contatto",
  "import.field.street": "Via (con numero civico)",
  "import.field.house_number": "Numero civico",
  "import.field.supplement": "Complemento d’indirizzo",
  "import.field.postal_code": "NAP",
  "import.field.city": "Località",
  "import.field.postal_city": "NAP e località",
  "import.field.country": "Paese",
  "import.field.email": "E-mail",
  "import.field.phone": "Telefono",
  "import.field.vat_number": "IDI / n. IVA",
  "import.field.buyer_reference": "Leitweg-ID / riferimento dell’acquirente",
  "import.field.group": "Gruppo",
  "import.field.default_currency": "Valuta",
  "import.field.default_language": "Lingua",
  "import.field.default_discount": "Sconto in %",
  "import.field.payment_terms_days": "Termine di pagamento in giorni",
  "import.field.tax_exempt": "Esente da IVA",
  "import.field.customer_since": "Cliente dal",
  "import.field.note": "Nota",
  "import.field.name": "Descrizione",
  "import.field.description": "Descrizione dettagliata",
  "import.field.unit": "Unità",
  "import.field.price": "Prezzo (valuta dell’azienda)",
  "import.field.price_chf": "Prezzo CHF",
  "import.field.price_eur": "Prezzo EUR",
  "import.field.tax_category": "Aliquota",
  "import.field.supply": "Merce/servizio",
  "import.result": "Risultato della prova",
  "import.summary": "{new} nuovi · {updated} aggiornati · {skipped} saltati · {error} con errori",
  "import.filter.label": "Mostra righe",
  "import.filter.all": "Tutte",
  "import.status.new": "Nuovo",
  "import.status.updated": "Aggiornato",
  "import.status.skipped": "Saltato",
  "import.status.error": "Errore",
  "import.report": "Scarica rapporto (CSV)",
  "import.reportFile.customers": "import-clienti.csv",
  "import.reportFile.articles": "import-articoli.csv",
  "import.noRows": "Nessuna riga in questa vista.",
  "import.moreRows": "Le prime {shown} di {total} righe; il rapporto le contiene tutte.",
  "import.errorsLeftOut": "Le righe con errori non vengono importate.",
  "import.row": "Riga",
  "import.statusTitle": "Risultato",
  "import.number": "N.",
  "import.name": "Nome",
  "import.detail.customers": "NAP e località",
  "import.detail.articles": "Prezzo",
  "import.problemTitle": "Nota",
  "import.problemText": "{field}: {reason}",
  "import.problemValue": "{field} «{value}»: {reason}",
  "import.problem.required": "mancante",
  "import.problem.invalid": "non valido",
  "import.problem.invalid_amount": "non è un importo",
  "import.problem.invalid_number": "non è un numero intero",
  "import.problem.invalid_date": "non è una data",
  "import.problem.invalid_flag": "né sì né no",
  "import.problem.unknown_unit": "unità sconosciuta",
  "import.problem.unknown_tax_rate": "aliquota sconosciuta",
  "import.problem.unknown_supply": "né merce né servizio",
  "import.problem.unknown_language":
    "lingua non supportata (tedesco, inglese, francese o italiano)",
  "import.problem.unknown_currency": "valuta non supportata (CHF o EUR)",
  "import.doneTitle": "Importazione completata",
  "import.show.customers": "Vai ai clienti",
  "import.show.articles": "Vai agli articoli",
  "import.again": "Importa un altro file",
  "import.title.invoices": "Riprendi vecchie fatture",
  "import.demo.title": "Nella demo l’importazione è disattivata",
  "import.demo.text":
    "Tutti i visitatori condividono questa demo, per questo non accetta file. Installi Rhinvoice sul suo computer o server – con Docker bastano pochi minuti. Lì potrà riprendere clienti, articoli e vecchie fatture con i relativi PDF.",
  "import.demo.install": "Guida all’installazione",
  "import.demo.guide": "Come funziona l’importazione",
  "import.descriptionInvoices":
    "Fatture di un programma usato in precedenza, con i loro PDF originali. Restano come erano: bloccate, con numero, importo e PDF.",
  "import.run.invoices": {
    zero: "Nessuna fattura da riprendere",
    one: "Riprendi 1 fattura",
    other: "Riprendi {count} fatture",
  },
  "import.done.invoices": {
    zero: "Nessuna fattura ripresa.",
    one: "1 fattura ripresa.",
    other: "{count} fatture riprese.",
  },
  "import.choose.invoices": "Elenco delle fatture (CSV o Excel)",
  "import.required.invoices":
    "Servono numero di fattura, data, importo e numero o nome del cliente. Rhinvoice riconosce la colonna dei numeri grazie ai file PDF.",
  "import.reportFile.invoices": "import-fatture.csv",
  "import.detail.invoices": "Importo",
  "import.show.invoices": "Vai alle fatture",
  "import.skippedInvoices":
    "Le fatture il cui numero esiste già vengono saltate: una fattura emessa non cambia mai.",
  "import.customer": "Cliente",
  "import.pdf": "PDF",
  "import.noPdf": "nessun PDF",
  "import.attachments": {
    zero: "nessun allegato",
    one: "+ 1 allegato",
    other: "+ {count} allegati",
  },
  "import.newCustomer": "Cliente creato (archiviato)",
  "import.pdfs.title": "File PDF",
  "import.pdfs.description":
    "Scelga la cartella con le fatture (con le sottocartelle) oppure singoli file PDF e ZIP. Rhinvoice cerca il numero di fattura nel nome del file; altri file con lo stesso numero (solleciti, copie) vengono aggiunti come allegati.",
  "import.pdfs.none": "Nessun file PDF ancora scelto; le fatture verranno importate senza PDF.",
  "import.pdfs.chosen": {
    zero: "Nessun file PDF nella cartella scelta",
    one: "1 file PDF · {size}",
    other: "{count} file PDF · {size}",
  },
  "import.pdfs.folder": "Scegli cartella",
  "import.pdfs.files": "Scegli file",
  "import.pdfs.clear": "Rimuovi i file PDF",
  "import.pdfs.tooMany":
    "Più di {max} file: comprima la cartella in un file ZIP e scelga quest’ultimo.",
  "import.pdfs.tooLarge": "Più di 200 MB in totale: importi i file in più parti.",
  "import.unused": {
    zero: "Tutti i file appartengono a una fattura",
    one: "1 file non appartiene a nessuna fattura dell’elenco",
    other: "{count} file non appartengono a nessuna fattura dell’elenco",
  },
  "import.unusedHint":
    "Manca una fattura nell’elenco, oppure il suo numero non figura nel nome del file? Un PDF si può aggiungere anche più tardi direttamente sulla fattura.",
  "import.nextNumber": "La prossima fattura che scriverà in Rhinvoice riceverà il numero {number}.",
  "import.problem.non_positive_total": "importo pari a zero o negativo",
  "import.problem.net_above_total": "netto superiore all’importo totale",
  "import.problem.open_above_total": "importo aperto superiore all’importo totale",
  "import.field.issue_date": "Data della fattura",
  "import.field.due_date": "Scadenza",
  "import.field.customer_number": "Numero cliente",
  "import.field.customer_name": "Nome del cliente",
  "import.field.currency": "Valuta",
  "import.field.net_amount": "Importo netto",
  "import.field.tax_amount": "Importo dell’imposta",
  "import.field.total_amount": "Importo totale",
  "import.field.open_amount": "Importo aperto",
  "import.field.subject": "Oggetto",
  "import.field.total": "Importo totale",
  "import.field.net": "Importo netto",
  "error.unreadable_file": "Impossibile leggere il file. Si tratta di un file CSV, Excel o ZIP?",
  "error.empty_file": "Il file è vuoto.",
  "error.file_too_large":
    "Il file è troppo grande: elenchi al massimo 5 MB, un PDF al massimo 20 MB, tutti i PDF insieme al massimo 200 MB.",
  "error.not_a_pdf": "Questo non è un file PDF.",
  "error.too_many_files": "Troppi file in una volta. Li importi come file ZIP o in più parti.",
  "error.imported_without_pdf": "Per questa fattura importata non c’è ancora un PDF.",
  "error.imported_document":
    "Una fattura importata consiste nel suo PDF; con essa questa operazione non è possibile.",
  "error.not_imported": "I file possono essere allegati solo alle fatture importate.",
  "error.too_many_rows":
    "Il file ha troppe righe. Lo suddivida in file di al massimo diecimila righe.",
  "error.too_many_columns": "Il file ha più di 100 colonne.",
  "error.company_not_set_up": "Configuri prima l’azienda.",
  "error.mapping_incomplete": "Mancano colonne senza le quali nessuna riga può essere importata.",
  "error.invalid_mapping": "L’associazione delle colonne non è valida.",
  "error.column_used_twice": "Una colonna è associata a due campi.",
  "error.unknown_column": "L’associazione indica una colonna che non esiste.",
  "error.unknown_field": "L’associazione indica un campo sconosciuto.",
  "error.empty_template": "Il modello non associa nessuna colonna.",
  "wizard.title": "Configura Rhinvoice",
  "wizard.steps": "Passi della configurazione",
  "wizard.stepCountry": "Paese",
  "wizard.stepCompany": "Azienda",
  "wizard.stepDone": "(completato)",
  "paymentCode.swiss_qr_bill": "Fattura QR",
  "paymentCode.epc": "GiroCode (codice QR EPC)",
  "wizard.countryTitle": "Dove ha sede la sua azienda?",
  "wizard.countryHint":
    "Il paese determina aliquote IVA, indicazioni obbligatorie, arrotondamento e il codice QR di pagamento sulla fattura.",
  "wizard.smallBusinessHint": "Fatture senza IVA, con l’indicazione prescritta.",
  "wizard.later": "Configura più tardi",
  "wizard.next": "Avanti",
  "wizard.back": "Indietro",
  "wizard.finish": "Salva e continua",
  "wizard.changeLater": "Tutto si può modificare più tardi nelle impostazioni.",
  "wizard.companyTitle": "La sua azienda",
  "wizard.companyHint": "Nome e indirizzo figurano come mittente su ogni fattura.",
  "wizard.taxNumberHint":
    "Rilasciato dall’ufficio delle imposte, se non c’è un n. di partita IVA UE.",
  "wizard.saved": "Azienda salvata. I prossimi passi si trovano nella panoramica.",
  "onboarding.title": "Benvenuto in Rhinvoice",
  "onboarding.progress": "Configurazione · {done} di {total} completati",
  "onboarding.done": "completato",
  "onboarding.open": "da fare",
  "onboarding.dismiss": "Nascondi lista di controllo",
  "onboarding.dismissed": "Lista di controllo nascosta.",
  "onboarding.step.company": "Paese e azienda",
  "onboarding.step.bank_account": "Registra un conto bancario",
  "onboarding.step.numbering": "Verifica le numerazioni",
  "onboarding.step.customers": "Registra o importa clienti",
  "onboarding.step.first_invoice": "Scrivi la prima fattura",
  "onboarding.step.backup": "Configura il backup",
  "onboarding.hint.company": "Mittente, aliquote IVA e indicazioni obbligatorie delle fatture.",
  "onboarding.hint.bank_account": "Per il codice QR di pagamento: fattura QR o GiroCode.",
  "onboarding.hint.numbering":
    "Proviene da un altro programma? Il prossimo numero di fattura può proseguire da lì.",
  "onboarding.hint.customers": "Uno alla volta oppure da un file CSV o Excel.",
  "onboarding.hint.first_invoice":
    "Con anteprima dal vivo; la fattura viene emessa solo su suo comando.",
  "onboarding.hint.backup": "Perché nessuna fattura vada persa, ad es. su Dropbox.",
  "onboarding.action.company": "Configura",
  "onboarding.action.bank_account": "Registra conto bancario",
  "onboarding.action.numbering": "Verifica",
  "onboarding.action.import": "Importa",
  "onboarding.action.customers": "Crea cliente",
  "onboarding.action.first_invoice": "Scrivi fattura",
  "onboarding.action.backup": "Mostra backup",
  "settings.tabPrint": "Layout di stampa",
  "printLayout.title": "Layout di stampa",
  "printLayout.description": "Come appaiono fatture, offerte e storni su carta e nel PDF.",
  "printLayout.template": "Modello",
  "printLayout.template.classic": "Classico",
  "printLayout.template.modern": "Moderno",
  "printLayout.template.plain": "Sobrio",
  "printLayout.templateHint.classic":
    "Linea sotto l’intestazione, intestazione della tabella grigia",
  "printLayout.templateHint.modern": "Colore aziendale come accento, totale evidenziato",
  "printLayout.templateHint.plain": "Molto spazio bianco, senza colore",
  "printLayout.logoAndFont": "Logo e carattere",
  "printLayout.logoPosition": "Logo",
  "printLayout.logoPosition.left": "A sinistra",
  "printLayout.logoPosition.right": "A destra",
  "printLayout.logoSize": "Dimensione del logo",
  "printLayout.logoSize.small": "Piccolo",
  "printLayout.logoSize.medium": "Medio",
  "printLayout.logoSize.large": "Grande",
  "printLayout.font": "Carattere",
  "printLayout.font.sans": "Senza grazie (Inter)",
  "printLayout.font.serif": "Con grazie (Source Serif)",
  "printLayout.colourHint": "Logo e colore aziendale si definiscono in Impostazioni → Azienda.",
  "printLayout.content": "Contenuto",
  "printLayout.senderLine": "Riga del mittente sopra l’indirizzo",
  "printLayout.senderLineHint": "La piccola riga nella finestra della busta.",
  "printLayout.contact": "Dati di contatto nell’intestazione",
  "printLayout.contactHint": "Telefono, e-mail e sito web sotto l’indirizzo in alto a destra.",
  "printLayout.positionNumbers": "Numeri di posizione",
  "printLayout.positionNumbersHint": "La colonna «Pos.» nella tabella.",
  "printLayout.footer": "Piè di pagina",
  "printLayout.footerAccounts": "Coordinate bancarie",
  "printLayout.footerAccounts.all": "Tutti i conti",
  "printLayout.footerAccounts.invoice_currency": "Solo valuta della fattura",
  "printLayout.footerAccounts.none": "Nessuna",
  "printLayout.footerHint":
    "I conti della stessa banca figurano insieme. I conti di terzi appaiono solo sui documenti pagati a loro. Il codice QR di pagamento usa sempre il conto su cui viene pagata la fattura.",
  "printLayout.reset": "Ripristina",
  "printLayout.issuedStay": "Vale per i nuovi PDF. I documenti emessi mantengono il loro PDF.",
  "printLayout.preview": "Anteprima del layout di stampa",
  "printLayout.previewHint": "Anteprima con la fattura più recente.",
  "settings.tabDunning": "Solleciti",
  "dunning.title": "Solleciti",
  "dunning.description": "Fatture scadute, i solleciti già scritti e il passo successivo.",
  "dunning.settings": "Imposta i livelli di sollecito",
  "dunning.emptyTitle": "Nulla di scaduto",
  "dunning.emptyDescription": "Non appena una fattura supera la data di scadenza, compare qui.",
  "dunning.table": "Fatture scadute",
  "dunning.invoice": "Fattura",
  "dunning.customer": "Cliente",
  "dunning.open": "Aperto",
  "dunning.overdue": "Scaduta",
  "dunning.reminders": "Scritti",
  "dunning.next": "Passo successivo",
  "dunning.days": { zero: "Da oggi", one: "Da 1 giorno", other: "Da {count} giorni" },
  "dunning.none": "Ancora nessuno",
  "dunning.level.1": "Promemoria di pagamento",
  "dunning.level.2": "1° sollecito",
  "dunning.level.3": "2° sollecito",
  "dunning.done":
    "Tutti i livelli sono stati scritti. Ulteriori passi (ad es. incasso) avvengono al di fuori di Rhinvoice.",
  "dunning.write": "Scrivi {level}",
  "dunning.written": "{level} scritto.",
  "dunning.openPdf": "Apri PDF",
  "dunning.dueSince": "Scaduta dal {date}",
  "dunning.dueFrom": "In scadenza dal {date}",
  "dunning.confirmTitle": "Scrivere {level} per {number}?",
  "dunning.confirmEarly": "Questo livello è dovuto solo a partire dal {date}.",
  "dunning.confirmDetails":
    "A {customer}, nuovo termine {deadline}, spese di sollecito {fee}. Il sollecito resta nella cronologia della fattura.",
  "dunning.problems": "Corregga i campi evidenziati.",
  "dunning.problem.days": "Numero intero da 0 a 365",
  "dunning.problem.paymentDays": "Numero intero da 1 a 365",
  "dunning.problem.fee": "Importo fino a 1000 in passi di 0.05",
  "dunning.problem.text": "Al massimo 2000 caratteri",
  "eInvoice.beforeEInvoices":
    "Il PDF è stato creato prima che Rhinvoice incorporasse le fatture elettroniche.",
  "eInvoice.issueWarning": "Questa fattura viene emessa senza fattura elettronica. Manca:",
  "eInvoice.issueXRechnungWarning": "Manca per la XRechnung (autorità tedesche):",
  "eInvoice.xrechnungSummary": "XRechnung per le autorità tedesche",
  "eInvoice.title": "Fattura elettronica",
  "eInvoice.embedded":
    "Il PDF contiene la fattura elettronica (ZUGFeRD, EN 16931): i programmi di contabilità leggono i dati direttamente.",
  "eInvoice.pending":
    "La fattura elettronica viene incorporata alla creazione del PDF (ZUGFeRD, EN 16931).",
  "eInvoice.notEmbedded": "Il PDF archiviato non contiene una fattura elettronica.",
  "eInvoice.disabled":
    "Al momento dell’emissione la fattura elettronica era disattivata nel layout di stampa.",
  "eInvoice.missing": "Per una fattura elettronica mancava al momento dell’emissione:",
  "eInvoice.xrechnung": "XRechnung (XML)",
  "eInvoice.xrechnungHint":
    "Per le autorità tedesche; la Leitweg-ID proviene dal cliente o dal riferimento della fattura.",
  "eInvoice.xrechnungMissing": "Per una XRechnung mancava al momento dell’emissione:",
  "eInvoice.nextTime":
    "Le fatture emesse restano invariate. Completi i dati per le fatture future.",
  "eInvoice.problem.seller_id_missing":
    "IDI, n. di partita IVA UE o iscrizione nel registro di commercio (Impostazioni → Azienda)",
  "eInvoice.problem.seller_vat_id_missing": "N. IVA (Impostazioni → Azienda)",
  "eInvoice.problem.seller_tax_id_missing":
    "N. IVA o codice fiscale tedesco (Impostazioni → Azienda)",
  "eInvoice.problem.buyer_vat_id_missing": "IDI o n. IVA con sigla del paese presso il cliente",
  "eInvoice.problem.buyer_reference_missing": "Leitweg-ID del cliente o riferimento della fattura",
  "eInvoice.problem.seller_email_missing": "E-mail (Impostazioni → Azienda)",
  "eInvoice.problem.seller_phone_missing": "Telefono (Impostazioni → Azienda)",
  "eInvoice.problem.buyer_email_missing": "E-mail presso il cliente",
  "eInvoice.problem.bank_account_missing": "Conto bancario (Impostazioni → Conti bancari)",
  "printLayout.eInvoiceTitle": "Fattura elettronica",
  "printLayout.eInvoice": "Fattura elettronica nel PDF (ZUGFeRD)",
  "printLayout.eInvoiceHint":
    "Le fatture e le fatture di storno emesse da ora in poi contengono i loro dati anche in formato leggibile da macchina (EN 16931). Il PDF ha lo stesso aspetto.",
  "error.e_invoice_incomplete": "Per una XRechnung mancano dei dati.",
  "error.not_an_e_invoice":
    "Solo le fatture e le fatture di storno emesse sono fatture elettroniche.",
  "documents.reminders": "Solleciti",
  "dunning.settingsTitle": "Livelli di sollecito",
  "dunning.settingsDescription":
    "Tre livelli: promemoria di pagamento, 1° e 2° sollecito. Ogni livello diventa dovuto dopo un certo numero di giorni e fissa un nuovo termine di pagamento.",
  "dunning.daysAfterInvoice": "Giorni dopo la scadenza",
  "dunning.daysAfterReminder": "Giorni dopo la fine dell’ultimo termine",
  "dunning.paymentDays": "Nuovo termine di pagamento in giorni",
  "dunning.fee": "Spese di sollecito",
  "dunning.feeHint":
    "Nella valuta della fattura, IVA esclusa. Solo se pattuite o per costi comprovati; 0 per nessuna.",
  "dunning.textDe": "Testo (tedesco)",
  "dunning.textEn": "Testo (inglese)",
  "dunning.textFr": "Testo (francese)",
  "dunning.textIt": "Testo (italiano)",
  "dunning.textHint":
    "Segnaposto: {faellig} (nuovo termine), {betrag}, {waehrung}, {nummer}, {kunde}.",
  "error.not_remindable": "Questa fattura non è scaduta o ha già ricevuto tutti i solleciti.",
  "error.levels_incomplete": "Servono tutti e tre i livelli di sollecito.",
  "recurring.short": "Ricorrente",
  "recurring.title": "Fatture ricorrenti",
  "recurring.description":
    "Fatture che Rhinvoice scrive da sé ogni mese, ogni trimestre o ogni anno.",
  "recurring.new": "Nuovo piano",
  "recurring.newDescription": "La fattura qui sotto viene creata di nuovo a ogni scadenza.",
  "recurring.emptyTitle": "Ancora nessuna fattura ricorrente",
  "recurring.emptyDescription":
    "Per hosting, manutenzione, abbonamenti o forfait: crei la fattura una volta sola, Rhinvoice la scrive di nuovo a ogni scadenza.",
  "recurring.name": "Denominazione",
  "recurring.nameHint": "Solo per lei, ad es. «Hosting Holzwerk».",
  "recurring.customer": "Cliente",
  "recurring.interval": "Intervallo",
  "recurring.intervalOption.monthly": "Mensile",
  "recurring.intervalOption.quarterly": "Trimestrale",
  "recurring.intervalOption.half_yearly": "Semestrale",
  "recurring.intervalOption.yearly": "Annuale",
  "recurring.nextDate": "Prossima fattura",
  "recurring.amount": "Importo",
  "recurring.action": "Alla scadenza",
  "recurring.actionShort.draft": "Bozza",
  "recurring.actionShort.issue": "Emetti",
  "recurring.actionShort.send": "Emetti e invia",
  "recurring.actionOption.draft": "Crea una bozza",
  "recurring.actionOption.issue": "Emetti",
  "recurring.actionOption.send": "Emetti e invia per e-mail",
  "recurring.actionHint.draft":
    "La bozza resta in attesa sotto Fatture finché non la verifica ed emette.",
  "recurring.actionHint.issue": "La fattura viene emessa con un numero; la invia lei stesso.",
  "recurring.actionHint.send":
    "La fattura viene emessa e inviata all’indirizzo e-mail del cliente.",
  "recurring.state": "Stato",
  "recurring.stateLabel.active": "Attivo",
  "recurring.stateLabel.paused": "In pausa",
  "recurring.stateLabel.finished": "Terminato",
  "recurring.stateLabel.problem": "Problema",
  "recurring.create": "Crea piano",
  "recurring.created": "Piano creato.",
  "recurring.planTitle": "Ripetizione",
  "recurring.planDescription": "Quando viene scritta la fattura e cosa succede poi.",
  "recurring.startDate": "Prima fattura il",
  "recurring.startDateHint":
    "Ogni fattura successiva segue secondo l’intervallo a partire da questo giorno (il 31 resta fine mese).",
  "recurring.endDate": "Ultima fattura entro il",
  "recurring.endDateHint": "Lasci vuoto finché non termina il piano.",
  "recurring.onTheDay": "Alla scadenza",
  "recurring.mailNotConfigured": "L’invio di e-mail non è ancora configurato.",
  "recurring.servicePeriod": "Inserisci il periodo della prestazione (ad es. 01.10.–31.10.)",
  "recurring.moreHint": "Oggetto, riferimento, valuta, lingua, sconto",
  "recurring.field.name": "Denominazione",
  "recurring.field.startDate": "Prima fattura il",
  "recurring.field.endDate": "Ultima fattura entro il",
  "recurring.problem.required": "Compili questo campo.",
  "recurring.problem.endBeforeStart": "La fine precede la prima fattura.",
  "recurring.statusTitle": "Stato",
  "recurring.finished": "Il piano è terminato: non seguiranno altre fatture.",
  "recurring.next": "Prossima fattura il {date}.",
  "recurring.nextWithPeriod": "Prossima fattura il {date}, periodo della prestazione fino al {to}.",
  "recurring.customerArchived":
    "Il cliente è archiviato, ma il piano continua a fatturargli. Metta in pausa il piano se non è più corretto.",
  "recurring.overriddenSource":
    "La fattura aveva un caso fiscale scelto manualmente. Le fatture ricorrenti lo determinano automaticamente; verifichi l’anteprima.",
  "recurring.pausedHint": "In pausa: le scadenze durante la pausa vengono saltate.",
  "recurring.noRuns": "Ancora nessuna fattura scritta.",
  "recurring.runs": "Fatture scritte",
  "recurring.outcome.draft": "Bozza",
  "recurring.outcome.issued": "Emessa",
  "recurring.outcome.sent": "Inviata",
  "recurring.outcome.failed": "Non riuscita",
  "recurring.openInvoice": "Apri fattura",
  "recurring.customerEmailMissing":
    "Non inviata: per il cliente non è registrato alcun indirizzo e-mail.",
  "recurring.interrupted": "Interrotto prima che la fattura fosse scritta.",
  "recurring.interruptedAfterCreate":
    "Interrotto dopo la creazione della fattura. Verifichi la fattura.",
  "recurring.pause": "Metti in pausa",
  "recurring.resume": "Riprendi",
  "recurring.paused": "Piano messo in pausa.",
  "recurring.resumed": "Piano ripreso.",
  "recurring.delete": "Elimina piano",
  "recurring.deleteTitle": "Eliminare il piano «{name}»?",
  "recurring.deleteDescription":
    "Non verranno scritte altre fatture. Le fatture già scritte restano invariate.",
  "recurring.deleted": "Piano «{name}» eliminato.",
  "recurring.makeRecurring": "Rendi ricorrente",
  "error.plan_start_in_past":
    "La prima fattura non può essere nel passato. Un nuovo intervallo richiede un nuovo primo giorno (oggi o più tardi).",
  "error.plan_not_paused": "Il piano non è in pausa.",
  "mail.sendAction": "Invia per e-mail",
  "mail.sendTitle": "Invia {title} per e-mail",
  "mail.sendDescription":
    "Verifichi destinatari e testo. Il PDF viene allegato e l’invio annotato nella cronologia.",
  "mail.notConfigured":
    "L’invio di e-mail non è ancora configurato. Indichi una volta il server di posta del suo provider e-mail.",
  "mail.setUp": "Configura e-mail",
  "mail.to": "A",
  "mail.toHint": "Separare più indirizzi con una virgola.",
  "mail.noCustomerAddress":
    "Per il cliente non è registrato alcun indirizzo e-mail. Separare più indirizzi con una virgola.",
  "mail.subject": "Oggetto",
  "mail.body": "Messaggio",
  "mail.attachments": "Allegato",
  "mail.attachXrechnung": "Allega anche la XRechnung (XML)",
  "mail.copyTo": "Una copia va a {address}.",
  "mail.send": "Invia",
  "mail.checkFields": "Verifichi i dati evidenziati.",
  "mail.cancelledWarning":
    "Questa fattura è stornata. La invii solo se il cliente ne ha nuovamente bisogno.",
  "mail.sending": "Invio in corso …",
  "mail.sent": "E-mail inviata a {to}.",
  "mail.recipientMissing": "Indichi almeno un indirizzo e-mail.",
  "mail.invalidRecipient": "«{address}» non è un indirizzo e-mail valido.",
  "mail.tooManyRecipients": "Al massimo {max} destinatari.",
  "mail.serverTitle": "Server di posta",
  "mail.serverDescription":
    "Rhinvoice invia tramite il server di posta in uscita (SMTP) del suo provider e-mail. I dati si trovano nella sua guida, ad es. Microsoft 365: smtp.office365.com, porta 587, STARTTLS. Gmail e iCloud richiedono una password per le app.",
  "mail.problems": "Verifichi i dati evidenziati.",
  "mail.host": "Server",
  "mail.hostHint": "Ad esempio smtp.suo-provider.ch",
  "mail.port": "Porta",
  "mail.security": "Crittografia",
  "mail.securityOption.starttls": "STARTTLS (di solito porta 587)",
  "mail.securityOption.tls": "SSL/TLS (di solito porta 465)",
  "mail.securityOption.none": "Nessuna",
  "mail.securityHint.starttls":
    "Consigliato: la connessione viene crittografata dopo essere stata stabilita.",
  "mail.securityHint.tls": "La connessione è crittografata fin dall’inizio.",
  "mail.securityHint.none":
    "Non crittografata. Da usare solo per un server di posta nella propria rete.",
  "mail.username": "Nome utente",
  "mail.usernameHint":
    "Di solito l’indirizzo e-mail. Lasciare vuoto se il server non richiede l’accesso.",
  "mail.password": "Password",
  "mail.passwordHint": "Viene salvata e non sarà mai più mostrata.",
  "mail.passwordKept": "È salvata una password. Lasciare vuoto per mantenerla.",
  "mail.sender": "Indirizzo del mittente",
  "mail.senderHint": "Molti provider inviano solo da indirizzi propri.",
  "mail.senderName": "Nome del mittente",
  "mail.replyTo": "Indirizzo di risposta",
  "mail.replyToHint": "Le risposte dei clienti vanno a questo indirizzo anziché al mittente.",
  "mail.copyToSender": "Invia una copia di ogni e-mail all’indirizzo del mittente",
  "mail.problem.required": "Compili questo campo.",
  "mail.problem.port": "Un numero tra 1 e 65535.",
  "mail.problem.email": "Indirizzo e-mail non valido.",
  "mail.problem.passwordWithoutUser": "Una password richiede un nome utente.",
  "mail.testTitle": "E-mail di prova",
  "mail.testDescription": "Verifichi i dati salvati con una breve e-mail.",
  "mail.testFirstSave": "Salvi prima il server di posta.",
  "mail.testTo": "Invia a",
  "mail.sendTest": "Invia e-mail di prova",
  "mail.testSent": "E-mail di prova inviata a {to}. Controlli la casella di posta in arrivo.",
  "mail.templatesTitle": "Modelli di e-mail",
  "mail.templatesDescription":
    "Oggetto e testo delle e-mail con cui vengono inviati documenti e solleciti. I segnaposto vengono compilati al momento dell’invio.",
  "mail.templateKind": "Per",
  "mail.templateLanguage": "Lingua",
  "mail.templateOwn": "Modello personalizzato.",
  "mail.templateBuiltIn": "Modello predefinito.",
  "mail.restoreTemplate": "Usa il modello predefinito",
  "mail.templateRestored": "Modello predefinito ripristinato.",
  "mail.kind.invoice": "Fattura",
  "mail.kind.quote": "Offerta",
  "mail.kind.cancellation": "Fattura di storno",
  "mail.kind.reminder_1": "Promemoria di pagamento",
  "mail.kind.reminder_2": "1° sollecito",
  "mail.kind.reminder_3": "2° sollecito",
  "mail.reminderSent": "{level} inviato per e-mail",

  "export.open": "Esporta",
  "export.title": "Esporta fatture",
  "export.description":
    "Le fatture e note di credito emesse in un periodo secondo la data della fattura, per la contabilità o la fiduciaria.",
  "export.period": "Periodo",
  "export.period.month": "Mese",
  "export.period.quarter": "Trimestre",
  "export.period.year": "Anno",
  "export.period.custom": "Libero",
  "export.month": "Mese",
  "export.quarter": "Trimestre",
  "export.quarterName": "{quarter}° trimestre",
  "export.year": "Anno",
  "export.from": "Dal",
  "export.to": "Al",
  "export.problem.missing": "Indichi entrambi i giorni.",
  "export.problem.reversed": "La fine precede l’inizio.",
  "export.problem.tooLong": "Al massimo dieci anni alla volta.",
  "export.format": "Formato",
  "export.format.xlsx": "Excel",
  "export.format.csv": "CSV",
  "export.format.pdf": "PDF",
  "export.withPdfs": "Allega i PDF delle fatture (ZIP)",
  "export.withPdfsHint":
    "Un file ZIP con l’elenco e il PDF di ogni fattura; la colonna «File» ne indica il nome.",
  "export.download": "Scarica",
  "export.preparing": "Preparazione …",
  "error.period_reversed": "Il periodo termina prima di iniziare.",
  "error.period_too_long": "Un periodo comprende al massimo dieci anni.",
  "error.export_busy": "Un’altra esportazione è in preparazione. Riprovi tra un momento.",
};
